import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import axios from "axios";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Camera, Eye, Cross, Smartphone, Home, Search, Video, Flame, Upload, X as XIcon, Bell, Lock, Newspaper, CreditCard, LogOut, ClipboardList, FileText, MessageSquare, Scale, Settings, Building2, Shield, Wallet, Briefcase, Clock, CalendarDays, Star, Zap, Sparkles, ThumbsUp, ChevronDown, Check, BadgeCheck, Share2, TrendingUp, ArrowRight, ChevronRight, SlidersHorizontal } from "lucide-react";
import { NewcomerChecklist } from "./components/NewcomerChecklist";

// Legal / static informational pages (extracted to keep App.js small)
import { PerformerAgreement } from "./pages/legal/PerformerAgreement";
import { PlatformRules } from "./pages/legal/PlatformRules";
import { HelpAndFAQ } from "./pages/legal/HelpAndFAQ";
import { AboutCompany } from "./pages/legal/AboutCompany";
import { PrivacyPolicy } from "./pages/legal/PrivacyPolicy";
import { TermsOfService } from "./pages/legal/TermsOfService";
import { Contacts } from "./pages/legal/Contacts";
import { RefundPolicy } from "./pages/legal/RefundPolicy";
import { LegalPage, getLegalSlug, applyLegacyLegalRedirect } from "./pages/legal/LegalRoutes";
import { ExecutorsProofBadge } from "./components/ExecutorsProofBadge";
import { B2B_CONFIRM_TOAST } from "./components/b2b/B2BBadges";
applyLegacyLegalRedirect();
import { AppReviewModal, UserProfileReviewsSection, AppReviewsSection, AppReviewsModal } from "./pages/reviews/AppReviews";
import { AdminMediaFilesSection, AdminArchivedMediaFilesSection, AdminExternalLinksSection, AdminPanel } from "./pages/admin/AdminPanel";
import { ConsentManagement, PrivacySettings } from "./pages/settings/ProfileSettings";
import { ReviewSection } from "./components/task/ReviewSection";
import { loadYandexMaps, getCurrentLocation } from "./utils/maps";
import { popModalsToDepth, closeAllModalsSilently } from "./utils/historyNav";
import { TaskMap } from "./components/task/TaskMap";
import { GraveCareChecklist } from "./components/task/GraveCareChecklist";
import { SecretShopperReport } from "./components/task/SecretShopperReport";
import { DisputeCustomerChoiceBlock } from "./components/task/DisputeCustomerChoiceBlock";
import { RevisionSubmitBlock } from "./components/task/RevisionSubmitBlock";
import { CreateTaskMap } from "./components/task/CreateTaskMap";
import { PasswordRecoveryScreen } from "./pages/auth/PasswordRecoveryScreen";
import { AuthScreen } from "./pages/auth/AuthScreen";
import { NotificationSettings } from "./pages/settings/NotificationSettings";
import { DiscussionsList } from "./pages/tasks/DiscussionsList";
import { TaskList } from "./pages/tasks/TaskList";
import { JournalistVerification } from "./pages/journalist/JournalistVerification";
import { JournalistSection } from "./pages/journalist/JournalistSection";
import { PaymentCardsSection } from "./pages/journalist/PaymentCardsSection";
import { IdentityVerification } from "./pages/executor/IdentityVerification";
import { CreateTask } from "./pages/tasks/CreateTask";
import { MyDisputes } from "./pages/tasks/MyDisputes";
import { MyTasks } from "./pages/tasks/MyTasks";
import { CommissionDashboardSection } from "./components/commission/CommissionInfo";
import { CountUp } from "./components/CountUp";
import { TaskResultsViewer } from "./components/task/TaskResultsViewer";
import { TaskChatOnly } from "./components/task/TaskChatOnly";
import { TaskChatOnlyEnhanced } from "./components/task/TaskChatOnlyEnhanced";
import { TaskChatModal } from "./components/task/TaskChatModal";
import { taskStatusLabel } from "./utils/statusLabels";
import { VerifiedExecutorBadge } from "./components/badges/VerifiedExecutorBadge";
import { BusinessStatusBadge } from "./components/b2b/B2BBadges";
import { B2BChatTip } from "./components/b2b/B2BChatTip";
import { BusinessStatusSettings } from "./pages/settings/BusinessStatusSettings";
import { uploadFileChunked, fileToBase64, CHUNKED_THRESHOLD } from "./utils/chunkedUpload";
import { formatMessageTime, formatServerDateTime } from "./utils/timeUtils";
import { needsSurcharge, isDirectPriceAgreed, fmtRub, isPriceChangeBlocking } from "./utils/priceChange";
import { UploadProgressBar } from "./components/ui/UploadProgressBar";
import { CookieBanner } from "./components/CookieBanner";
import { ShareServiceModal } from "./components/ShareServiceModal";
import { AddToScreenBanner } from "./components/AddToScreenBanner";
import { EmailVerifyBanner } from "./components/EmailVerifyBanner";
import { NotificationBell } from "./components/notifications/NotificationBell";

// Ж12: переход из уведомления к заданию — «Мои задания» откроет карточку по rv_focus_task
const focusTaskThen = (setCurrentView) => (taskId) => {
  try { sessionStorage.setItem("rv_focus_task", taskId); } catch (e) {}
  setCurrentView("my-tasks");
};

// Захват UTM-меток из URL при загрузке приложения (до того как диплинки очистят адресную строку).
// Используется как источник регистрации для админ-аналитики (лендинги, шеринг, реклама).
try {
  const _utmParams = new URLSearchParams(window.location.search);
  const _utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach((k) => {
    const v = _utmParams.get(k);
    if (v) _utm[k] = v.slice(0, 100);
  });
  if (Object.keys(_utm).length > 0) localStorage.setItem("rv_reg_utm", JSON.stringify(_utm));
} catch (e) {}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Принудительно устанавливаем базовый URL для axios
axios.defaults.baseURL = BACKEND_URL;
console.log(' Axios baseURL установлен:', axios.defaults.baseURL);

// Password Protection Component
const PasswordForm = ({ onSuccess }) => {
 const [password, setPassword] = useState('');
 const [error, setError] = useState('');

 const handleSubmit = (e) => {
 e.preventDefault();
 if (password === 'reversia2025') {
 localStorage.setItem('site_access', 'granted');
 onSuccess();
 } else {
 setError('Неправильный пароль');
 setPassword('');
 }
 };

 return (
 <div className="min-h-screen flex" style={{background: 'var(--rv-paper)'}}>
 {/* Left — dark panel */}
 <div
 className="hidden lg:flex flex-col justify-between w-1/2 p-16"
 style={{background: 'var(--rv-navy)', color: 'white'}}
 >
 <div>
 <div className="flex items-center gap-3 mb-16">
 <img
 src="/images/logo.png"
 alt="Реверсия"
 className="w-12 h-12 rounded-lg object-cover"
 style={{background: 'rgba(255,255,255,0.08)', padding: '4px'}}
 onError={e => { e.target.style.display='none'; }}
 />
 <span className="text-xl font-heading font-bold tracking-tight" style={{fontFamily:'Manrope,sans-serif'}}>Реверсия</span>
 </div>
 <h1 className="text-4xl font-heading font-bold leading-tight mb-6" style={{fontFamily:'Manrope,sans-serif', color:'white', letterSpacing:'-0.02em'}}>Маркетплейс<br/>заданий на местах
 </h1>
 <p style={{color: 'rgba(255,255,255,0.65)', lineHeight: '1.7', fontSize: '1.0625rem'}}>Заказчики публикуют задания — фото, видео, аудиты, тайный покупатель.<br/>Исполнители выполняют и загружают результат.
 </p>
 </div>
 <p style={{color: 'rgba(255,255,255,0.3)', fontSize: '0.8125rem'}}>Скоро полный запуск для всех
 </p>
 </div>

 {/* Right — form panel */}
 <div className="flex flex-col justify-center items-center flex-1 p-8">
 {/* Mobile logo */}
 <div className="lg:hidden flex items-center gap-2 mb-10">
 <img src="/images/logo.png" alt="Реверсия" className="w-8 h-8 rounded object-contain"
 style={{background: 'var(--rv-teal)', padding:'2px'}}
 onError={e=>{e.target.style.display='none';}} />
 <span className="text-xl font-bold" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-teal)'}}>Реверсия</span>
 </div>

 <div className="w-full max-w-sm">
 <h2 className="text-2xl font-bold mb-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>Закрытый доступ
 </h2>
 <p className="mb-8 text-sm" style={{color:'var(--rv-text-secondary)'}}>Сайт находится в разработке. Введите пароль для входа.
 </p>

 <form onSubmit={handleSubmit} className="space-y-5">
 <div>
 <label className="block text-sm font-medium mb-1.5" style={{color:'var(--rv-text-primary)'}}>Пароль доступа
 </label>
 <input
 data-testid="password-gate-input"
 type="password"
 value={password}
 onChange={e =>setPassword(e.target.value)}
 className="w-full px-4 py-3 rounded-lg text-sm border focus:outline-none"
 style={{
 border: '1px solid var(--rv-border)',
 background: 'white',
 color: 'var(--rv-text-primary)',
 transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
 }}
 placeholder="Введите пароль"
 required
 autoFocus
 />
 {error && (
 <p className="mt-2 text-sm" style={{color: 'var(--rv-error)'}}>{error}</p>
 )}
 </div>

 <button
 data-testid="password-gate-submit"
 type="submit"
 className="reversia-btn-primary w-full"
 style={{borderRadius:'0.5rem', padding:'0.75rem', fontSize:'0.9375rem'}}
 >Войти
 </button>
 </form>
 </div>
 </div>
 </div>
 );
};

// Toast notification component
const Toast = ({ message, type = "info", onClose }) => {
 const bgColor = type === "success" ? "bg-green-500" : type === "error" ? "bg-red-500" : type === "warning" ? "bg-orange-500" : "bg-blue-500";
 
 return (
 <div className={`fixed top-[72px] right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg max-w-sm`} style={{zIndex:10050}} data-testid="app-toast" role="status">
 <div className="flex items-center justify-between">
 <span>{message}</span>
 <button 
 onClick={onClose}
 className="ml-3 text-white hover:text-gray-200"
 >
 ×
 </button>
 </div>
 </div>
 );
};

// Favicon badge — показывает счётчик непрочитанных на иконке вкладки
let _cleanFaviconDataUrl = null;

// Предзагружаем оригинальный favicon как data URL сразу при запуске
const _preloadCleanFavicon = () => {
 const linkEl = document.querySelector("link[rel~='icon']");
 const src = (linkEl && linkEl.getAttribute('href')) || '/favicon.ico';
 if (src.startsWith('data:')) {
 _cleanFaviconDataUrl = src;
 return;
 }
 const img = new Image();
 img.crossOrigin = 'anonymous';
 img.onload = () => {
 const c = document.createElement('canvas');
 c.width = c.height = 32;
 c.getContext('2d').drawImage(img, 0, 0, 32, 32);
 _cleanFaviconDataUrl = c.toDataURL('image/png');
 };
 img.onerror = () => {
 const c = document.createElement('canvas');
 c.width = c.height = 32;
 const ctx = c.getContext('2d');
 ctx.fillStyle = '#0f766e';
 ctx.fillRect(0, 0, 32, 32);
 _cleanFaviconDataUrl = c.toDataURL('image/png');
 };
 img.src = src;
};
_preloadCleanFavicon();

const _setFaviconDataUrl = (dataUrl) => {
 document.querySelectorAll("link[rel~='icon']").forEach(el =>el.remove());
 const link = document.createElement('link');
 link.rel = 'icon';
 link.type = 'image/png';
 link.href = dataUrl;
 document.head.appendChild(link);
};

export const updateFaviconBadge = (count) => {
 document.title = count > 0 ? `(${count > 99 ? '99+' : count}) Reversia` : 'Reversia';

 if (count <= 0) {
 if (_cleanFaviconDataUrl) _setFaviconDataUrl(_cleanFaviconDataUrl);
 return;
 }

 const drawWithBadge = (sourceDataUrl) => {
 const canvas = document.createElement('canvas');
 canvas.width = canvas.height = 32;
 const ctx = canvas.getContext('2d');
 const img = new Image();
 img.onload = () => {
 ctx.drawImage(img, 0, 0, 32, 32);
 ctx.beginPath();
 ctx.arc(23, 8, 9, 0, 2 * Math.PI);
 ctx.fillStyle = '#ef4444';
 ctx.fill();
 ctx.fillStyle = '#ffffff';
 ctx.font = 'bold 11px Arial';
 ctx.textAlign = 'center';
 ctx.textBaseline = 'middle';
 ctx.fillText(count > 9 ? '9+' : String(count), 23, 8);
 _setFaviconDataUrl(canvas.toDataURL('image/png'));
 };
 img.src = sourceDataUrl;
 };

 if (_cleanFaviconDataUrl) {
 drawWithBadge(_cleanFaviconDataUrl);
 } else {
 const wait = setInterval(() => {
 if (_cleanFaviconDataUrl) { clearInterval(wait); drawWithBadge(_cleanFaviconDataUrl); }
 }, 100);
 }
};

// Global Message Notifier Component
const GlobalMessageNotifier = ({ user, showToast, setCurrentView, openTaskChat }) => {
 const [hasNewMessages, setHasNewMessages] = useState(false);
 const [newMessageInfo, setNewMessageInfo] = useState(null);

 useEffect(() => {
 const checkForNewMessages = async () => {
 try {
 const response = await axios.get(`${API}/my-discussions`);
 const discussions = response.data;
 
 // Check if any discussions have unread messages
 const totalUnread = discussions.reduce((sum, task) =>sum + (task.unread_count || 0), 0);
 
 // Only show if unread count is greater than what user last dismissed
 const lastDismissedCount = parseInt(sessionStorage.getItem('last_dismissed_unread') || '0', 10);
 
 if (totalUnread > 0 && totalUnread >lastDismissedCount) {
 const taskWithMessages = discussions.find(task =>task.unread_count > 0);
 setHasNewMessages(true);
 setNewMessageInfo({
 count: totalUnread,
 taskTitle: taskWithMessages?.title || 'Задание',
 taskId: taskWithMessages?.id || null
 });
 updateFaviconBadge(totalUnread);
 } else if (totalUnread === 0) {
 // Reset dismiss counter when all messages are read
 sessionStorage.setItem('last_dismissed_unread', '0');
 setHasNewMessages(false);
 setNewMessageInfo(null);
 updateFaviconBadge(0);
 }
 } catch (error) {
 console.error('GlobalMessageNotifier: Error checking for new messages:', error);
 }
 };

 // Check immediately
 checkForNewMessages();
 
 // Check every 10 seconds
 const interval = setInterval(checkForNewMessages, 10000);

 // При возврате на вкладку — сразу перепроверить
 const onVisible = () => { if (!document.hidden) checkForNewMessages(); };
 document.addEventListener('visibilitychange', onVisible);
 
 return () => {
 clearInterval(interval);
 document.removeEventListener('visibilitychange', onVisible);
 };
 }, []);

 if (!hasNewMessages) return null;

 return (
 <div 
 data-testid="global-message-notifier"
 className="text-white py-2 px-4 text-center cursor-pointer transition-colors"
 style={{backgroundColor: 'var(--reversia-teal)'}}
 onClick={() => {
 setHasNewMessages(false);
 // Save current count as dismissed threshold
 sessionStorage.setItem('last_dismissed_unread', String(newMessageInfo?.count || 0));
 updateFaviconBadge(0);
 if (newMessageInfo?.taskId && openTaskChat) {
 openTaskChat(newMessageInfo.taskId);
 } else if (setCurrentView) {
 setCurrentView('discussions');
 }
 }}
 >
 <div className="flex items-center justify-center gap-2">
 <span>У вас {newMessageInfo?.count} {newMessageInfo?.count === 1 ? 'новое сообщение' : 'новых сообщений'} в "{newMessageInfo?.taskTitle}" — нажмите для перехода в чат
 </span>
 <button
 onClick={(e) => {
 e.stopPropagation();
 setHasNewMessages(false);
 sessionStorage.setItem('last_dismissed_unread', String(newMessageInfo?.count || 0));
 updateFaviconBadge(0);
 }}
 className="ml-4 bg-white bg-opacity-20 px-2 py-1 rounded text-sm hover:bg-opacity-30"
 >
 <XIcon size={14} />
 </button>
 </div>
 </div>
 );
};

// Push Notifications Helper Functions
export const registerServiceWorker = async () => {
 if ('serviceWorker' in navigator) {
 try {
 // Register service worker for push notifications
 const registration = await navigator.serviceWorker.register('/sw.js');
 console.log('Service Worker registered:', registration);
 return registration;
 } catch (error) {
 console.error('Service Worker registration failed:', error);
 return null;
 }
 }
 return null;
};

// Show notification for new messages
export const showMessageNotification = (senderName, message, taskTitle) => {
 // Create session-based unique notification ID
 const notificationId = `${senderName}-${message.slice(0, 10)}-${taskTitle}`;
 const sessionKey = `session_notified_${notificationId}`;
 
 // Check if this exact notification was already shown in current session
 if (sessionStorage.getItem(sessionKey)) {
 console.log('Notification already shown in this session:', notificationId);
 return;
 }

 // Check if mobile device
 const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
 
 if (isMobile) {
 // For mobile, show toast notification instead of browser notification
 console.log('Mobile notification:', senderName, message);
 } else {
 // Desktop browser notifications
 if ('Notification' in window && Notification.permission === 'granted') {
 const notification = new Notification(` Новое сообщение от ${senderName}`, {
 body: `${taskTitle}: ${message}`,
 icon: '/favicon.ico',
 tag: notificationId, // Use unique tag
 requireInteraction: false,
 silent: false
 });

 // Mark as shown in session
 sessionStorage.setItem(sessionKey, 'true');

 // Auto-close after 5 seconds
 setTimeout(() => {
 notification.close();
 }, 5000);

 // Handle notification close
 notification.onclose = () => {
 console.log('Notification closed:', notificationId);
 };
 }
 }
};

export const requestNotificationPermission = async () => {
 if ('Notification' in window) {
 const permission = await Notification.requestPermission();
 console.log('Notification permission:', permission);
 return permission === 'granted';
 }
 return false;
};

export const subscribeToPushNotifications = async () => {
 try {
 // 1. Request notification permission
 const hasPermission = await requestNotificationPermission();
 if (!hasPermission) throw new Error('Notification permission denied');

 // 2. Ensure service worker is registered
 const registration = await registerServiceWorker();
 if (!registration) throw new Error('Service worker not available');

 // 3. Get VAPID public key from backend
 const token = localStorage.getItem('token');
 const vapidRes = await axios.get(`${API}/notifications/vapid-public-key`, {
 headers: { Authorization: `Bearer ${token}` }
 });
 const vapidPublicKey = vapidRes.data.public_key;

 // 4. Subscribe via browser PushManager
 const subscription = await registration.pushManager.subscribe({
 userVisibleOnly: true,
 applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
 });

 // 5. Extract keys from subscription
 const subscriptionJSON = subscription.toJSON();
 const p256dh = subscriptionJSON.keys?.p256dh || '';
 const auth = subscriptionJSON.keys?.auth || '';

 // 6. Save subscription to backend
 await axios.post(`${API}/notifications/push-subscribe`, {
 endpoint: subscriptionJSON.endpoint,
 p256dh_key: p256dh,
 auth_key: auth
 }, { headers: { Authorization: `Bearer ${token}` } });

 console.log('Push subscription saved successfully');
 return true;
 } catch (error) {
 console.error('Push subscription failed:', error);
 return false;
 }
};


export const unsubscribeFromPushNotifications = async () => {
 try {
 if (!('serviceWorker' in navigator)) return false;
 const registration = await navigator.serviceWorker.ready;
 const subscription = await registration.pushManager.getSubscription();
 if (!subscription) return true;

 const subscriptionJSON = subscription.toJSON();
 const token = localStorage.getItem('token');

 await axios.delete(`${API}/notifications/push-subscribe`, {
 data: {
 endpoint: subscriptionJSON.endpoint,
 p256dh_key: subscriptionJSON.keys?.p256dh || '',
 auth_key: subscriptionJSON.keys?.auth || ''
 },
 headers: { Authorization: `Bearer ${token}` }
 });

 await subscription.unsubscribe();
 return true;
 } catch (error) {
 console.error('Unsubscribe failed:', error);
 return false;
 }
};

export const updateNotificationLocation = async (latitude, longitude, onError = null) => {
 try {
 // Проверяем есть ли токен авторизации
 const token = localStorage.getItem('token');
 if (!token) {
 console.log('Skipping notification location update - user not authenticated');
 if (onError) onError('Для обновления уведомлений необходима авторизация');
 return true; // Не считаем ошибкой если не авторизован
 }

 await axios.post(`${API}/notifications/update-location`, {
 latitude,
 longitude
 }, {
 headers: {
 'Authorization': `Bearer ${token}`
 }
 });
 console.log('Notification location updated successfully');
 if (onError) onError(null); // Сбрасываем ошибку при успехе
 return true;
 } catch (error) {
 console.error('Failed to update notification location (non-critical):', error);
 
 let errorMessage = 'Не удалось обновить местоположение для уведомлений';
 if (error.response?.status === 401) {
 errorMessage = 'Необходима повторная авторизация для обновления уведомлений';
 } else if (error.response?.status === 500) {
 errorMessage = 'Временная проблема сервера при обновлении уведомлений';
 }
 
 if (onError) onError(errorMessage);
 // Не блокируем процесс геолокации из-за ошибки уведомлений
 return true; // Возвращаем true чтобы не прерывать геолокацию
 }
};

// Helper function to convert VAPID key
export const urlBase64ToUint8Array = (base64String) => {
 const padding = '='.repeat((4 - base64String.length % 4) % 4);
 const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
 const rawData = window.atob(base64);
 const outputArray = new Uint8Array(rawData.length);
 for (let i = 0; i < rawData.length; ++i) {
 outputArray[i] = rawData.charCodeAt(i);
 }
 return outputArray;
};

// Geolocation Hook
export const useGeolocation = () => {
 const [location, setLocation] = useState(null);
 const [error, setError] = useState(null);
 const [loading, setLoading] = useState(false);
 const [timeoutId, setTimeoutId] = useState(null);
 const [watchId, setWatchId] = useState(null);
 const [currentStep, setCurrentStep] = useState('');

 const getCurrentLocation = () => {
 if (!navigator.geolocation) {
 setError('Геолокация не поддерживается вашим браузером');
 return;
 }

 setLoading(true);
 setError(null);
 setCurrentStep('Определение через GPS...');
 
 // Стратегия 1: Высокая точность (5 секунд)
 const highAccuracyTimeout = setTimeout(() => {
 setCurrentStep('Пробуем стандартную точность...');
 tryLowAccuracy();
 }, 5000);

 setTimeoutId(highAccuracyTimeout);
 
 navigator.geolocation.getCurrentPosition(
 (position) => {
 clearTimeout(highAccuracyTimeout);
 const newLocation = {
 latitude: position.coords.latitude,
 longitude: position.coords.longitude
 };
 setLocation(newLocation);
 setError(null);
 setLoading(false);
 setCurrentStep('');
 
 updateNotificationLocation(newLocation.latitude, newLocation.longitude, (errorMsg) => {
 if (errorMsg) {
 console.warn('Notification location update failed:', errorMsg);
 }
 });
 },
 (error) => {
 clearTimeout(highAccuracyTimeout);
 setCurrentStep('Пробуем стандартную точность...');
 tryLowAccuracy();
 },
 { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 }
 );
 };

 const tryLowAccuracy = () => {
 // Стратегия 2: Низкая точность (8 секунд)
 const lowAccuracyTimeout = setTimeout(() => {
 setCurrentStep('Пробуем определение по IP...');
 tryIPGeolocation();
 }, 8000);

 setTimeoutId(lowAccuracyTimeout);

 navigator.geolocation.getCurrentPosition(
 (position) => {
 clearTimeout(lowAccuracyTimeout);
 const newLocation = {
 latitude: position.coords.latitude,
 longitude: position.coords.longitude
 };
 setLocation(newLocation);
 setError(null);
 setLoading(false);
 setCurrentStep('');
 
 updateNotificationLocation(newLocation.latitude, newLocation.longitude, (errorMsg) => {
 if (errorMsg) {
 console.warn('Notification location update failed:', errorMsg);
 }
 });
 },
 (error) => {
 clearTimeout(lowAccuracyTimeout);
 setCurrentStep('Пробуем определение по IP...');
 tryIPGeolocation();
 },
 { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
 );
 };

 const tryIPGeolocation = async () => {
 // Стратегия 3: IP-геолокация
 try {
 const response = await fetch('https://ipapi.co/json/');
 const data = await response.json();
 
 if (data.latitude && data.longitude) {
 const newLocation = {
 latitude: data.latitude,
 longitude: data.longitude
 };
 setLocation(newLocation);
 setError(null);
 setLoading(false);
 setCurrentStep('');
 
 updateNotificationLocation(newLocation.latitude, newLocation.longitude, (errorMsg) => {
 if (errorMsg) {
 console.warn('Notification location update failed:', errorMsg);
 }
 });
 return;
 }
 } catch (ipError) {
 console.error('IP geolocation failed:', ipError);
 }

 // Если все стратегии провалились
 setError('Не удалось определить местоположение. Попробуйте разрешить доступ к геолокации в браузере или введите координаты вручную.');
 setLoading(false);
 setCurrentStep('');
 };

 const setManualLocation = (lat, lng) => {
 const newLocation = {
 latitude: parseFloat(lat),
 longitude: parseFloat(lng)
 };
 setLocation(newLocation);
 setError(null);
 updateNotificationLocation(newLocation.latitude, newLocation.longitude, (errorMsg) => {
 if (errorMsg) {
 console.warn('Notification location update failed:', errorMsg);
 }
 });
 };

 const cancelLocationRequest = () => {
 if (timeoutId) {
 clearTimeout(timeoutId);
 setTimeoutId(null);
 }
 if (watchId) {
 navigator.geolocation.clearWatch(watchId);
 setWatchId(null);
 }
 setLoading(false);
 setCurrentStep('');
 setError('Определение местоположения отменено');
 };

 const startWatching = () => {
 if (!navigator.geolocation) {
 setError('Геолокация не поддерживается вашим браузером');
 return null;
 }

 const id = navigator.geolocation.watchPosition(
 (position) => {
 const newLocation = {
 latitude: position.coords.latitude,
 longitude: position.coords.longitude
 };
 setLocation(newLocation);
 setError(null);
 
 updateNotificationLocation(newLocation.latitude, newLocation.longitude, (errorMsg) => {
 if (errorMsg) {
 console.warn('Notification location update failed:', errorMsg);
 }
 });
 },
 (error) => {
 let errorMessage = 'Ошибка отслеживания местоположения';
 switch(error.code) {
 case error.PERMISSION_DENIED:
 errorMessage = 'Доступ к геолокации заблокирован';
 break;
 case error.POSITION_UNAVAILABLE:
 errorMessage = 'Информация о местоположении недоступна';
 break;
 case error.TIMEOUT:
 errorMessage = 'Время ожидания истекло';
 break;
 default:
 errorMessage = error.message;
 break;
 }
 setError(errorMessage);
 },
 { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
 );
 
 setWatchId(id);
 return id;
 };

 return { location, error, loading, currentStep, getCurrentLocation, startWatching, cancelLocationRequest, setManualLocation };
};

// Auth Context
export const AuthContext = React.createContext();

// ======================================================================
// UserAvatar component — shows avatar image or initials placeholder
// ======================================================================
export const UserAvatar = ({ user, size = 40, className = "", userId = null, onClick = null }) => {
 const [imgError, setImgError] = React.useState(false);
 const initials = (user?.name || "?").split(" ").map(w =>w[0]).join("").substring(0, 2).toUpperCase();

 let avatarUrl = null;
 if (!imgError) {
 const ver = user?.avatar_gridfs_id ? `?v=${encodeURIComponent(user.avatar_gridfs_id)}` : '';
 if (user?.avatar_url) {
 avatarUrl = `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}${ver}`;
 } else if (userId) {
 avatarUrl = `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/avatar`;
 }
 }

 return (
 <div
 className={`rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${className} ${onClick ? 'cursor-pointer' : ''}`}
 style={{
 width: size,
 height: size,
 background: avatarUrl ? '#e5e7eb' : 'var(--reversia-teal, #0d9488)',
 fontSize: size * 0.38,
 fontWeight: 700,
 color: avatarUrl ? undefined : '#fff',
 border: '2px solid #fff',
 boxShadow: '0 1px 4px rgba(0,0,0,0.12)'
 }}
 onClick={onClick || undefined}
 title={onClick ? "Нажмите для просмотра" : undefined}
 >
 {avatarUrl
 ? <img src={avatarUrl} alt={user?.name || ""} style={{width: '100%', height: '100%', objectFit: 'cover'}} onError={() =>setImgError(true)} />
 : <span style={{color: '#fff'}}>{initials}</span>}
 </div>
 );
};

// ======================================================================
// AvatarViewModal — всплывающий просмотр аватарки собеседника
// ======================================================================
export const AvatarViewModal = ({ userId, name, statusText, onClose }) => {
 if (!userId) return null;
 const avatarUrl = `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/avatar`;
 return (
 <div
 className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
 onClick={onClose}
 >
 <div
 className="bg-white rounded-2xl shadow-2xl p-5 flex flex-col items-center gap-3 max-w-xs w-full mx-4"
 onClick={e =>e.stopPropagation()}
 >
 <img
 src={avatarUrl}
 alt={name}
 className="rounded-full object-cover"
 style={{width: 160, height: 160, border: '3px solid #e5e7eb'}}
 onError={e => { e.target.style.display = 'none'; }}
 />
 <p className="font-semibold text-gray-800 text-base text-center">{name}</p>
 {statusText && <p className="text-sm text-gray-500 text-center italic">"{statusText}"</p>}
 <button
 onClick={onClose}
 className="text-xs text-gray-400 hover:text-gray-600 underline mt-1"
 >Закрыть
 </button>
 </div>
 </div>
 );
};

// ======================================================================
// AvatarUpload component — кликабельная аватарка с hover-эффектом + кнопка удаления + статус
// ======================================================================
export const AvatarUpload = ({ user, onAvatarUpdated, showToast, size = 96 }) => {
 const { fetchUser } = React.useContext(AuthContext);
 const [uploading, setUploading] = React.useState(false);
 const [deleting, setDeleting] = React.useState(false);
 const [editingStatus, setEditingStatus] = React.useState(false);
 const [statusDraft, setStatusDraft] = React.useState(user?.status_text || '');
 const [savingStatus, setSavingStatus] = React.useState(false);
 const inputRef = React.useRef(null);

 React.useEffect(() => {
 setStatusDraft(user?.status_text || '');
 }, [user?.status_text]);

 const handleFileChange = async (e) => {
 const file = e.target.files[0];
 if (!file) return;
 if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
 showToast("Разрешены только JPEG, PNG и WEBP", "error");
 return;
 }
 if (file.size > 5 * 1024 * 1024) {
 showToast("Файл должен быть меньше 5 МБ", "error");
 return;
 }
 setUploading(true);
 try {
 const formData = new FormData();
 formData.append("file", file);
 await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/users/upload-avatar`, formData, {
 headers: { "Content-Type": "multipart/form-data" }
 });
 if (fetchUser) await fetchUser();
 if (onAvatarUpdated) onAvatarUpdated();
 showToast("Аватарка обновлена", "success");
 } catch (err) {
 showToast(err.response?.data?.detail || "Ошибка загрузки аватарки", "error");
 } finally {
 setUploading(false);
 e.target.value = "";
 }
 };

 const handleDelete = async () => {
 if (!window.confirm("Удалить фото профиля?")) return;
 setDeleting(true);
 try {
 await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/avatar`);
 if (fetchUser) await fetchUser();
 if (onAvatarUpdated) onAvatarUpdated();
 showToast("Фото удалено", "success");
 } catch (err) {
 showToast(err.response?.data?.detail || "Ошибка удаления фото", "error");
 } finally {
 setDeleting(false);
 }
 };

 const handleSaveStatus = async () => {
 if (statusDraft.length > 150) {
 showToast("Статус не может быть длиннее 150 символов", "error");
 return;
 }
 setSavingStatus(true);
 try {
 await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/profile`, null, {
 params: { status_text: statusDraft }
 });
 if (fetchUser) await fetchUser();
 setEditingStatus(false);
 showToast("Статус сохранён", "success");
 } catch (err) {
 showToast(err.response?.data?.detail || "Ошибка сохранения статуса", "error");
 } finally {
 setSavingStatus(false);
 }
 };

 const hasAvatar = !!(user?.avatar_url || user?.avatar_gridfs_id);

 return (
 <div className="flex flex-col items-center gap-2" style={{minWidth: size}}>
 {/* Кликабельная аватарка */}
 <div
 data-testid="avatar-upload-btn"
 className="relative cursor-pointer group flex-shrink-0"
 style={{width: size, height: size}}
 onClick={() => !uploading && !deleting && inputRef.current?.click()}
 title="Нажмите, чтобы изменить фото"
 >
 <UserAvatar user={user} size={size} />
 {/* Hover-оверлей с иконкой камеры */}
 {!uploading && !deleting && (
 <div
 className="absolute inset-0 rounded-full flex items-center justify-center transition-opacity duration-200 opacity-0 group-hover:opacity-100"
 style={{background: 'rgba(0,0,0,0.38)'}}
 >
 <svg width={size * 0.36} height={size * 0.36} fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
 <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
 <circle cx="12" cy="13" r="4"/>
 </svg>
 </div>
 )}
 {/* Спиннер при загрузке/удалении */}
 {(uploading || deleting) && (
 <div className="absolute inset-0 rounded-full flex items-center justify-center" style={{background: 'rgba(0,0,0,0.45)'}}>
 <div className="rounded-full border-2 border-white border-t-transparent animate-spin" style={{width: size * 0.32, height: size * 0.32}} />
 </div>
 )}
 </div>
 {/* Кнопка удаления — только при наличии фото */}
 {hasAvatar && (
 <button
 data-testid="avatar-delete-btn"
 onClick={handleDelete}
 disabled={uploading || deleting}
 className="text-xs px-2 py-0.5 rounded border border-red-200 text-red-500 hover:bg-red-50 disabled:opacity-50 transition-colors"
 style={{fontSize: 10}}
 >
 {deleting ? "..." : "Удалить фото"}
 </button>
 )}
 {/* Статус/девиз */}
 {editingStatus ? (
 <div className="flex flex-col items-center gap-1 w-full" style={{maxWidth: 200}}>
 <input
 type="text"
 value={statusDraft}
 onChange={e =>setStatusDraft(e.target.value)}
 maxLength={150}
 placeholder="Ваш статус или девиз..."
 className="w-full text-xs border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:border-teal-400"
 autoFocus
 />
 <div className="flex gap-1">
 <button
 onClick={handleSaveStatus}
 disabled={savingStatus}
 className="text-xs px-2 py-0.5 rounded bg-teal-500 text-white hover:bg-teal-600 disabled:opacity-50"
 >
 {savingStatus ? "..." : "Сохранить"}
 </button>
 <button
 onClick={() => { setEditingStatus(false); setStatusDraft(user?.status_text || ''); }}
 className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
 >Отмена
 </button>
 </div>
 </div>
 ) : (
 <div
 className="text-center cursor-pointer group"
 style={{maxWidth: 200}}
 onClick={() =>setEditingStatus(true)}
 title="Нажмите, чтобы добавить статус"
 >
 {user?.status_text
 ? <p className="text-xs text-gray-500 italic group-hover:text-teal-600 transition-colors">"{user.status_text}"</p>
 : <p className="text-xs text-gray-400 group-hover:text-teal-500 transition-colors">+ добавить статус</p>
 }
 </div>
 )}
 <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} className="hidden" />
 </div>
 );
};



// Helper function to get card styling classes based on task type
import { getTaskCardClasses } from "./utils/taskStyles";
import { PublicProfileToggle } from "./components/settings/PublicProfileToggle";

const AuthProvider = ({ children }) => {
 const [user, setUser] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const token = localStorage.getItem("token");
 if (token) {
 axios.defaults.headers.Authorization = `Bearer ${token}`;
 fetchUser();
 } else {
 setLoading(false);
 }
 }, []);

 // A3: профиль обновляется без перелогина — при возврате во вкладку (например, после подтверждения email в соседней)
 // и по сигналу rv_profile_changed из других вкладок (смена бизнес-статуса и т.п.)
 useEffect(() => {
 let last = 0;
 const refresh = () => {
 if (!localStorage.getItem("token") || Date.now() - last < 10000) return;
 last = Date.now();
 fetchUser();
 };
 const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
 const onStorage = (e) => { if (e.key === 'rv_profile_changed') { last = 0; refresh(); } };
 window.addEventListener('focus', refresh);
 document.addEventListener('visibilitychange', onVisible);
 window.addEventListener('storage', onStorage);
 return () => {
 window.removeEventListener('focus', refresh);
 document.removeEventListener('visibilitychange', onVisible);
 window.removeEventListener('storage', onStorage);
 };
 }, []);

 const fetchUser = async () => {
 try {
 const response = await axios.get(`${API}/auth/me`);
 setUser(response.data);
 // Сохраняем пользователя в localStorage для восстановления при сетевых ошибках
 localStorage.setItem('lastKnownUser', JSON.stringify(response.data));
 } catch (error) {
 // Удаляем токен только при ошибке авторизации (401), а не при всех ошибках
 if (error.response?.status === 401) {
 console.log(' Token expired or invalid, logging out');
 localStorage.removeItem("token");
 localStorage.removeItem("lastKnownUser");
 delete axios.defaults.headers.Authorization;
 setUser(null);
 } else {
 // При других ошибках (сеть, сервер и т.д.) НЕ разлогиниваем пользователя
 console.log(' Network/server error, keeping user logged in:', error.message);
 
 // ИСПРАВЛЕНИЕ: При сетевых ошибках сохраняем последнего известного пользователя
 const lastUser = localStorage.getItem('lastKnownUser');
 if (lastUser) {
 try {
 setUser(JSON.parse(lastUser));
 console.log(' Восстановлен пользователь из локального хранилища');
 } catch (e) {
 console.log(' Ошибка восстановления пользователя:', e);
 }
 }
 }
 } finally {
 setLoading(false);
 }
 };

 const login = async (email, password) => {
 try {
 const response = await axios.post(`${API}/auth/login`, { email, password });
 const { token, user } = response.data;
 localStorage.setItem("token", token);
 axios.defaults.headers.Authorization = `Bearer ${token}`;
 setUser(user);
 // Сохраняем пользователя в localStorage для восстановления при сетевых ошибках
 localStorage.setItem('lastKnownUser', JSON.stringify(user));
 
 // Register service worker and request notification permission after login
 registerServiceWorker();
 requestNotificationPermission();
 
 return { success: true };
 } catch (error) {
 return { success: false, error: error.response?.data?.detail || "Login failed" };
 }
 };

 const register = async (userData) => {
 try {
 const response = await axios.post(`${API}/auth/register`, userData);
 return { success: true };
 } catch (error) {
 return { success: false, error: error.response?.data?.detail || "Registration failed" };
 }
 };

 const logout = async () => {
 try {
 await axios.post(`${API}/auth/logout`);
 } catch (e) {
 // Даже если запрос не прошёл — чистим локальное состояние
 }
 localStorage.removeItem("token");
 localStorage.removeItem("lastKnownUser");
 delete axios.defaults.headers.Authorization;
 setUser(null);
 };

 return (
 <AuthContext.Provider value={{ user, setUser, login, register, logout, loading, fetchUser }}>
 {children}
 </AuthContext.Provider>
 );
};

// Компонент обратного отсчёта для системы приоритетов
export const PriorityTimer = ({ initialSeconds, onExpired }) => {
 const [seconds, setSeconds] = React.useState(initialSeconds);

 React.useEffect(() => {
 if (seconds <= 0) {
 if (onExpired) onExpired();
 return;
 }
 const interval = setInterval(() => {
 setSeconds(prev => {
 if (prev <= 1) {
 clearInterval(interval);
 if (onExpired) onExpired();
 return 0;
 }
 return prev - 1;
 });
 }, 1000);
 return () =>clearInterval(interval);
 }, []);

 if (seconds <= 0) return null;

 const mins = Math.floor(seconds / 60);
 const secs = seconds % 60;
 const display = mins > 0
 ? `${mins} мин ${secs.toString().padStart(2, '0')} сек`
 : `${secs} сек`;

 return (
 <div data-testid="priority-lock-banner" className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
 <div className="flex items-center gap-2 mb-1">
 <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
 </svg>
 <span className="text-sm font-semibold text-amber-800">Задание появится через {display}
 </span>
 </div>
 <p className="text-xs text-amber-700 leading-snug">Исполнителям с рейтингом 4.5 и выше задания доступны сразу в момент размещения. Повышайте рейтинг, чтобы получать задания первыми.
 </p>
 </div>
 );
};

// Main App Component
const App = () => {
 // Конфиг доступа к сайту приходит с бэкенда (управляется из AdminPanel).
 // null = ещё загружается; затем { site_gate_enabled, paid_tasks_enabled }
 const [siteConfig, setSiteConfig] = useState(null);
 const [accessGranted, setAccessGranted] = useState(() => localStorage.getItem('site_access') === 'granted');

 useEffect(() => {
 let active = true;
 axios.get(`${API}/public/site-config`)
 .then(res => { if (active) setSiteConfig(res.data); })
 .catch(() => { if (active) setSiteConfig({ site_gate_enabled: false, paid_tasks_enabled: false }); });
 return () => { active = false; };
 }, []);

 const handlePasswordSuccess = () => {
 setAccessGranted(true);
 };

 // Пока грузится конфиг — лёгкий брендовый лоадер (без вспышки контента)
 if (siteConfig === null) {
 return (
 <div className="min-h-screen flex flex-col items-center justify-center" style={{background: 'var(--rv-paper)'}}>
 <img src="/images/logo.png" alt="Реверсия" className="w-12 h-12 rounded-lg object-contain mb-4" onError={e => { e.target.style.display='none'; }} />
 <div className="animate-spin rounded-full h-7 w-7 border-b-2" style={{borderColor:'var(--rv-teal)'}}></div>
 </div>
 );
 }

 // Парольная блокировка включается администратором (на всякий случай)
 if (siteConfig.site_gate_enabled && !accessGranted) {
 return <PasswordForm onSuccess={handlePasswordSuccess} />;
 }

 // Сайт публичный — показываем приложение
 return (
 <AuthProvider>
 <div className="min-h-screen" style={{background: 'var(--rv-paper)'}}>
 <MainContent />
 </div>
 </AuthProvider>
 );
};

// Main Content Component
const MainContent = () => {
 const { user, loading, fetchUser } = React.useContext(AuthContext);
 // Восстанавливаем последний просмотренный раздел при обновлении страницы
 const [currentView, setCurrentViewState] = useState("home");
 const currentViewRef = useRef("home");

 // Навигация по разделам создаёт запись истории браузера: жест "назад" возвращает на предыдущий раздел
 const setCurrentView = useCallback((view) => {
 const hadModals = closeAllModalsSilently();
 if (view !== currentViewRef.current || hadModals > 0) {
 const url = window.location.pathname + window.location.search;
 // При открытой модалке перезаписываем её запись, чтобы "назад" вёл на предыдущий раздел, а не в модалку
 if (hadModals > 0) window.history.replaceState({ rvView: view }, '', url);
 else window.history.pushState({ rvView: view }, '', url);
 }
 currentViewRef.current = view;
 setCurrentViewState(view);
 localStorage.setItem('currentView', view);
 }, []);

 // Диплинки (?view=, ?chat=, возврат из ЮКасса): переключение раздела БЕЗ новой записи истории + очистка URL
 const applyDeepLinkView = useCallback((view) => {
 currentViewRef.current = view;
 setCurrentViewState(view);
 localStorage.setItem('currentView', view);
 window.history.replaceState({ rvView: view }, '', window.location.pathname);
 }, []);

 // Стартовая запись истории получает метку раздела (для корректного возврата "назад"/"вперёд").
 // Зависимость от user: AuthScreen очищает URL через replaceState({}) и стирает метку — восстанавливаем после логина.
 useEffect(() => {
 const st = window.history.state || {};
 if (!st.rvView) {
 window.history.replaceState({ ...st, rvView: currentViewRef.current }, '', window.location.pathname + window.location.search);
 }
 }, [user]);

 // Жест/кнопка "назад": сначала закрывается верхняя модалка, затем переключается раздел без перезагрузки
 useEffect(() => {
 const onPopState = (e) => {
 if (popModalsToDepth(e.state)) return;
 const view = (e.state && e.state.rvView) || 'home';
 currentViewRef.current = view;
 setCurrentViewState(view);
 localStorage.setItem('currentView', view);
 };
 window.addEventListener('popstate', onPopState);
 return () => window.removeEventListener('popstate', onPopState);
 }, []);

 // При смене раздела прокручиваем страницу наверх (разделы из футера/меню открываются сначала)
 useEffect(() => {
 window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
 }, [currentView]);
 const [createTaskCategory, setCreateTaskCategory] = useState("general");
 const [createTaskSubcategory, setCreateTaskSubcategory] = useState("photo_video");
 const selectCreateTask = (category, subcategory = "photo_video") => { setCreateTaskCategory(category); setCreateTaskSubcategory(subcategory); };
 // Выход из формы создания сбрасывает предвыбор: прочие входы в 'create' открывают общий вид по умолчанию
 useEffect(() => { if (currentView !== "create") selectCreateTask("general"); }, [currentView]);
 const [toast, setToast] = useState(null);
 const [disputes, setDisputes] = useState([]); // Add disputes state for header count
 const [showAppReviewModal, setShowAppReviewModal] = useState(false);
 const [showShareModal, setShowShareModal] = useState(false);
 const [reviewTaskId, setReviewTaskId] = useState(null);
 const [deepLinkChatTaskId, setDeepLinkChatTaskId] = useState(null);

 // Fetch disputes for header counter
 const fetchDisputes = useCallback(async () => {
 if (!user) return;
 try {
 const response = await axios.get(`${API}/disputes/my`);
 setDisputes(response.data);
 } catch (error) {
 console.error("Error fetching disputes:", error);
 }
 }, [user]);

 // Load disputes on user change
 useEffect(() => {
 fetchDisputes();
 }, [fetchDisputes]);

 // Обработка URL параметров после возврата из ЮКасса
 useEffect(() => {
 const params = new URLSearchParams(window.location.search);
 const paymentStatus = params.get('payment_status');
 const view = params.get('view');
 const taskId = params.get('task_id');
 
 if (paymentStatus === 'success' && view) {
 // Показываем уведомление об успешной оплате
 showToast("Оплата прошла успешно! Задание опубликовано и доступно исполнителям.", "success", 10000);
 // Переключаем на нужную вкладку без лишней записи истории, очищая URL
 applyDeepLinkView(view);
 } else if (paymentStatus === 'surcharge_success' && view) {
 // Показываем уведомление об успешной оплате доплаты
 showToast("Доплата прошла успешно. Цена задания обновлена.", "success", 10000);
 applyDeepLinkView(view);
 }
 }, []);

 // Deep-link из email: /?chat=TASK_ID — открываем вкладку "Чат" и нужный диалог после входа
 useEffect(() => {
 if (!user) return;
 const params = new URLSearchParams(window.location.search);
 const chatTaskId = params.get('chat');
 if (chatTaskId) {
 setDeepLinkChatTaskId(chatTaskId);
 applyDeepLinkView('discussions');
 return;
 }
 const paymentStatus = params.get('payment_status');
 const viewParam = params.get('view');
 const allowedViews = ['tasks', 'my-tasks', 'disputes', 'discussions', 'notifications', 'create', 'admin', 'identity-verification', 'business-status', 'home'];
 if (!paymentStatus && viewParam && allowedViews.includes(viewParam)) {
 applyDeepLinkView(viewParam);
 }
 }, [user, applyDeepLinkView]);

 // Открыть чат конкретного задания (клик по баннеру/уведомлению на дашборде)
 const openTaskChat = useCallback((taskId) => {
 if (!taskId) return;
 setDeepLinkChatTaskId(taskId);
 setCurrentView('discussions');
 }, [setCurrentView]);

 const showToast = (message, type = "info", duration = 8000) => {
 setToast({ message, type });
 setTimeout(() =>setToast(null), duration);
 };

 const closeToast = () => {
 setToast(null);
 };

 const legalSlug = getLegalSlug(window.location.pathname);
 if (legalSlug) return <LegalPage slug={legalSlug} user={user} />;

 if (loading) {
 return (
 <div className="flex items-center justify-center min-h-screen" style={{background: 'var(--rv-paper)'}}>
 <div className="flex flex-col items-center gap-4">
 <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{borderColor: 'var(--rv-teal)', borderTopColor: 'transparent'}}></div>
 <p className="text-sm" style={{color: 'var(--rv-text-muted)'}}>Загрузка...</p>
 </div>
 </div>
 );
 }

 if (!user) {
 return (<><AuthScreen setCurrentView={setCurrentView} currentView={currentView} /><CookieBanner /></>);
 }

 return (
 <div className="min-h-screen" style={{background: 'var(--rv-paper)'}}>
 <Header user={user} setCurrentView={setCurrentView} currentView={currentView} disputes={disputes} onShare={() => setShowShareModal(true)} />
 {/* Global notification banner for new messages — скрыт на главной (там есть карточки) */}
 {user && currentView !== 'home' && (
 <GlobalMessageNotifier user={user} showToast={showToast} setCurrentView={setCurrentView} openTaskChat={openTaskChat} />
 )}
 
 {user && <EmailVerifyBanner user={user} showToast={showToast} />}

 <main className="rv-main-content w-full max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
 {currentView === "home" && <Dashboard user={user} setCurrentView={setCurrentView} showToast={showToast} selectCreateTask={selectCreateTask} openTaskChat={openTaskChat} onShare={() => setShowShareModal(true)} />}
 {currentView === "tasks" && <TaskList user={user} showToast={showToast} setCurrentView={setCurrentView} />}
 {currentView === "create" && <CreateTask user={user} setCurrentView={setCurrentView} showToast={showToast} defaultServiceType={createTaskCategory} defaultSubcategory={createTaskSubcategory} />}
 {currentView === "my-tasks" && <MyTasks user={user} showToast={showToast} setShowAppReviewModal={setShowAppReviewModal} setReviewTaskId={setReviewTaskId} />}
 {currentView === "disputes" && <MyDisputes user={user} showToast={showToast} />}
 {currentView === "discussions" && (
 <DiscussionsList
 user={user}
 showToast={showToast}
 initialOpenTaskId={deepLinkChatTaskId}
 onInitialChatOpened={() => setDeepLinkChatTaskId(null)}
 />
 )}
 {currentView === "notifications" && <NotificationSettings user={user} showToast={showToast} onNavigate={setCurrentView} />}
 {currentView === "privacy-settings" && <PrivacySettings user={user} showToast={showToast} />}
 {currentView === "journalist" && <JournalistSection user={user} showToast={showToast} onNavigate={setCurrentView} />}
 {currentView === "identity-verification" && <IdentityVerification user={user} showToast={showToast} onBack={() => setCurrentView("home")} />}
 {currentView === "business-status" && <BusinessStatusSettings user={user} showToast={showToast} onSaved={fetchUser} />}
 {currentView === "payment-cards" && <PaymentCardsSection user={user} showToast={showToast} setCurrentView={setCurrentView} />}
 {currentView === "help" && <HelpAndFAQ setCurrentView={setCurrentView} />}
 {currentView === "about" && <AboutCompany />}
 {currentView === "privacy" && <PrivacyPolicy setCurrentView={setCurrentView} />}
 {currentView === "terms" && <TermsOfService setCurrentView={setCurrentView} />}
 {currentView === "performer-agreement" && <PerformerAgreement />}
 {currentView === "platform-rules" && <PlatformRules />}
 {currentView === "contacts" && <Contacts setCurrentView={setCurrentView} />}
 {currentView === "refund-policy" && <RefundPolicy />}
 {currentView === "admin" && (user.role === "admin" || user.is_admin) && (
 <AdminPanel user={user} showToast={showToast} />
 )}
 </main>
 {toast && (
 <Toast 
 message={toast.message} 
 type={toast.type} 
 onClose={() =>setToast(null)} 
 />
 )}

 {/* App Review Modal */}
 <AppReviewModal 
 isOpen={showAppReviewModal}
 onClose={() =>setShowAppReviewModal(false)}
 taskId={reviewTaskId}
 />

 {/* Модалка "Рассказать о Реверсии" */}
 {showShareModal && (
 <ShareServiceModal user={user} showToast={showToast} onClose={() => setShowShareModal(false)} />
 )}

 {/* Баннер "Добавить на экран" (только мобильные) */}
 <AddToScreenBanner />
 </div>
 );
};

// Header Component
const Header = ({ user, setCurrentView, currentView, disputes = [], onShare }) => {
 const { logout } = React.useContext(AuthContext);
 const [showUserMenu, setShowUserMenu] = useState(false);
 const userMenuRef = React.useRef(null);

 // Закрытие дропдауна при клике вне — без оверлея
 React.useEffect(() => {
 if (!showUserMenu) return;
 const handleClick = (e) => {
 if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
 setShowUserMenu(false);
 }
 };
 document.addEventListener('mousedown', handleClick);
 return () =>document.removeEventListener('mousedown', handleClick);
 }, [showUserMenu]);
 
 const activeDisputesCount = disputes.filter(d =>d.status === 'pending' || d.status === 'manual_review'
 ).length;

 const navItems = [
 { id: 'home', label: 'Главная', shortLabel: 'Главная', Icon: Home },
 { id: 'tasks', label: 'Задания', shortLabel: 'Задания', Icon: ClipboardList },
 { id: 'my-tasks', label: 'Мои задания', shortLabel: 'Мои', Icon: FileText },
 { id: 'discussions', label: 'Чат', shortLabel: 'Чат', Icon: MessageSquare },
 { id: 'disputes', label: 'Споры', shortLabel: 'Споры', Icon: Scale, badge: activeDisputesCount },
 ];

 // Ж19 (TZ-7 п.1): значок «Уведомления» убран из шапки — единый колокольчик NotificationBell; настройки — через его дропдаун и меню «Ещё»
 const secondaryNavItems = [
 { id: 'privacy-settings', label: 'Приватность', Icon: Shield },
 ...(user.role === "executor" ? [
 { id: 'journalist', label: 'Журналист', Icon: Newspaper },
 { id: 'identity-verification', label: 'Верификация личности', Icon: BadgeCheck },
 { id: 'business-status', label: 'Бизнес-статус', Icon: Building2 },
 { id: 'payment-cards', label: 'Выплаты', Icon: Wallet },
 ] : []),
 ...((user.role === "admin" || user.is_admin) ? [
 { id: 'admin', label: 'Управление', Icon: Settings },
 ] : []),
 ];

 return (
 <>
 {/* ===== ДЕСКТОПНЫЙ ХЕДЕР ===== */}
 <header className="rv-header hidden sm:block">
 <div className="max-w-7xl mx-auto px-6">
 <div className="flex items-center justify-between h-14">
 {/* Логотип */}
 <button
 onClick={() =>setCurrentView("home")}
 className="flex items-center gap-2.5 flex-shrink-0"
 style={{background:'none', border:'none', padding:0, cursor:'pointer'}}
 data-testid="header-logo-btn"
 >
 <img
 src="/images/logo.png"
 alt="Реверсия"
 className="w-12 h-12 rounded-lg object-cover"
 style={{background: 'var(--rv-teal)', padding:'1px'}}
 onError={e => { e.target.style.display='none'; }}
 />
 <span
 className="text-lg font-bold"
 style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-teal)', letterSpacing:'-0.01em'}}
 >Реверсия
 </span>
 </button>

 {/* Основная навигация */}
 <nav className="flex items-center gap-1">
 {navItems.map(({ id, label, Icon, badge }) => (
 <button
 key={id}
 data-testid={`nav-${id}-btn`}
 onClick={() =>setCurrentView(id)}
 className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-base font-medium transition-colors"
 style={{
 background: currentView === id ? 'rgba(13,148,136,0.1)' : 'transparent',
 color: currentView === id ? 'var(--rv-teal)' : 'var(--rv-text-secondary)',
 border: 'none',
 cursor: 'pointer',
 fontFamily: 'IBM Plex Sans, sans-serif',
 }}
 >
 <Icon size={16} />
 {label}
 {badge > 0 && (
 <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-white flex items-center justify-center"
 style={{background: 'var(--rv-error)', fontSize: '0.6rem', fontWeight:700}}>
 {badge}
 </span>
 )}
 </button>
 ))}
 {/* Разделитель */}
 <div className="w-px h-4 mx-1" style={{background:'var(--rv-border)'}} />
 {secondaryNavItems.map(({ id, label, Icon }) => (
 <button
 key={id}
 data-testid={`nav-${id}-btn`}
 onClick={() =>setCurrentView(id)}
 title={label}
 className="relative p-1.5 rounded-md transition-colors icon-tooltip"
 data-tooltip={label}
 style={{
 background: currentView === id ? 'rgba(13,148,136,0.1)' : 'transparent',
 color: currentView === id ? 'var(--rv-teal)' : 'var(--rv-text-secondary)',
 border: 'none',
 cursor: 'pointer',
 }}
 >
 <Icon size={15} />
 </button>
 ))}
 </nav>

 {/* Пользователь */}
 <div className="flex items-center gap-1 flex-shrink-0">
 <NotificationBell testPrefix="header-bell" onOpenTask={focusTaskThen(setCurrentView)} onOpenAll={() => setCurrentView('notifications')} />
 <div className="relative flex-shrink-0" ref={userMenuRef}>
 <button
 data-testid="user-menu-btn"
 onClick={() =>setShowUserMenu(!showUserMenu)}
 className="flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors"
 style={{background:'transparent', border:'none', cursor:'pointer'}}
 >
 <UserAvatar user={user} size={28} />
 <div className="text-left hidden md:block">
 <p className="text-xs font-medium" style={{color:'var(--rv-text-primary)', fontFamily:'IBM Plex Sans,sans-serif', lineHeight:'1.2'}}>
 {user.name}
 </p>
 <div className="flex items-center gap-1">
 <p className="text-xs" style={{color:'var(--rv-text-muted)'}}>
 {user.role === "customer" ? "Заказчик" : user.role === "admin" ? "Администратор" : "Исполнитель"}
 </p>
 {user.role === "executor" && user.is_verified_executor && (
 <VerifiedExecutorBadge size="xs" showLabel={false} />
 )}
 </div>
 </div>
 <ChevronDown size={14} style={{color:'var(--rv-text-muted)'}} />
 </button>

 {/* Дропдаун */}
 {showUserMenu && (
 <div
 className="absolute right-0 top-full mt-1 w-48 rounded-lg shadow-lg overflow-hidden"
 style={{background:'white', border:'1px solid var(--rv-border)', zIndex:200}}
 >
 <div className="py-1">
 <button
 data-testid="user-menu-share-btn"
 onClick={() => { setShowUserMenu(false); onShare && onShare(); }}
 className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm transition-colors"
 style={{color:'var(--rv-text-primary)', background:'none', border:'none', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
 >
 <Share2 size={14} style={{color:'var(--rv-teal)'}} />Рассказать друзьям
 </button>
 <div style={{height:'1px', background:'var(--rv-border)', margin:'2px 0'}} />
 <button
 onClick={() => { logout(); setShowUserMenu(false); }}
 className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm transition-colors"
 style={{color:'var(--rv-error)', background:'none', border:'none', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
 >
 <LogOut size={14} />Выйти из аккаунта
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 </header>

 {/* ===== МОБИЛЬНЫЙ ХЕДЕР (верх) ===== */}
 <header className="sm:hidden rv-header">
 <div className="flex items-center justify-between px-4 h-12">
 <button
 onClick={() =>setCurrentView("home")}
 className="flex items-center gap-2"
 style={{background:'none', border:'none', cursor:'pointer', padding:0}}
 >
 <img src="/images/logo.png" alt="Реверсия" className="w-9 h-9 rounded object-cover"
 style={{padding:'0px'}}
 onError={e=>{e.target.style.display='none';}} />
 <span className="text-sm font-bold" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-teal)'}}>Реверсия</span>
 </button>
 <div className="flex items-center gap-2">
 <NotificationBell testPrefix="mobile-bell" onOpenTask={focusTaskThen(setCurrentView)} onOpenAll={() => setCurrentView('notifications')} />
 <button
 onClick={logout}
 className="flex items-center gap-1 text-xs px-2 py-1 rounded"
 style={{color:'var(--rv-error)', background:'rgba(220,38,38,0.08)', border:'none', cursor:'pointer'}}
 >
 <LogOut size={12} />Выйти
 </button>
 </div>
 </div>
 </header>

 {/* ===== МОБИЛЬНЫЙ ТАБ-БАР (низ) ===== */}
 <nav className="rv-mobile-tabbar sm:hidden">
 {navItems.map(({ id, label, shortLabel, Icon, badge }) => (
 <button
 key={id}
 data-testid={`mobile-nav-${id}-btn`}
 onClick={() =>setCurrentView(id)}
 className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full"
 style={{
 background: 'none',
 border: 'none',
 cursor: 'pointer',
 color: currentView === id ? 'var(--rv-teal)' : 'var(--rv-text-muted)',
 position: 'relative',
 }}
 >
 <Icon size={20} strokeWidth={currentView === id ? 2.5 : 1.75} />
 <span style={{fontSize:'0.72rem', fontFamily:'IBM Plex Sans,sans-serif', fontWeight: currentView === id ? 600 : 400}}>
 {shortLabel || label}
 </span>
 {badge > 0 && (
 <span className="absolute top-1 right-2 w-4 h-4 rounded-full text-white flex items-center justify-center"
 style={{background:'var(--rv-error)', fontSize:'0.6rem', fontWeight:700}}>
 {badge}
 </span>
 )}
 </button>
 ))}
 {/* Доп. меню */}
 <button
 data-testid="mobile-nav-more-btn"
 onClick={() =>setShowUserMenu(!showUserMenu)}
 className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full"
 style={{
 background: 'none',
 border: 'none',
 cursor: 'pointer',
 color: 'var(--rv-text-muted)',
 }}
 >
 <Settings size={20} strokeWidth={1.75} />
 <span style={{fontSize:'0.72rem', fontFamily:'IBM Plex Sans,sans-serif'}}>Ещё</span>
 </button>
 </nav>

 {/* Мобильный дропдаун "Ещё" */}
 {showUserMenu && (
 <>
 <div className="fixed inset-0 sm:hidden" style={{zIndex:9998}} onClick={() =>setShowUserMenu(false)} />
 <div className="fixed bottom-16 left-2 right-2 rounded-2xl shadow-xl overflow-hidden sm:hidden"
 style={{background:'white', border:'1px solid var(--rv-border)', zIndex:9999, willChange:'transform'}}
      onMouseDown={e => e.stopPropagation()}>
 <div className="p-2 space-y-0.5">
 {[{ id: 'notifications', label: 'Настройки уведомлений', Icon: SlidersHorizontal }, ...secondaryNavItems].map(({ id, label, Icon }) => (
 <button key={id} data-testid={`mobile-menu-${id}-btn`}
 onClick={() => { setCurrentView(id); setShowUserMenu(false); }}
 className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm"
 style={{
 background: currentView === id ? 'rgba(13,148,136,0.08)' : 'none',
 color: currentView === id ? 'var(--rv-teal)' : 'var(--rv-text-primary)',
 border: 'none', cursor: 'pointer',
 fontFamily: 'IBM Plex Sans, sans-serif', fontWeight: currentView === id ? 600 : 400,
 }}
 >
 <Icon size={18} />{label}
 </button>
 ))}
 <button
 data-testid="mobile-menu-share-btn"
 onClick={() => { setShowUserMenu(false); onShare && onShare(); }}
 className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm"
 style={{background:'none', color:'var(--rv-text-primary)', border:'none', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
 >
 <Share2 size={18} style={{color:'var(--rv-teal)'}} />Рассказать друзьям
 </button>
 <div style={{height:'1px', background:'var(--rv-border)', margin:'4px 0'}} />
 <button
 onClick={() => { logout(); setShowUserMenu(false); }}
 className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm"
 style={{background:'none', color:'var(--rv-error)', border:'none', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
 >
 <LogOut size={18} />Выйти
 </button>
 </div>
 </div>
 </>
 )}
 </>
 );
};

// Notification Settings Component
// Dashboard Component
const AnnouncementBanner = () => {
  const [announcement, setAnnouncement] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/announcements/current`);
        if (mounted) setAnnouncement(res.data.announcement || null);
      } catch (e) {
        // non-critical
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleDismiss = async () => {
    if (!announcement) return;
    const id = announcement.id;
    setAnnouncement(null);
    try {
      await axios.post(`${API}/announcements/${id}/dismiss`);
    } catch (e) {
      // non-critical
    }
  };

  if (!announcement) return null;

  return (
    <div
      data-testid="announcement-banner"
      className="rounded-2xl p-5 flex items-start gap-4"
      style={{ background: 'var(--rv-navy)', color: 'white', boxShadow: '0 10px 30px rgba(15,23,42,0.18)' }}
    >
      <div
        className="flex-shrink-0 rounded-xl flex items-center justify-center"
        style={{ width: 44, height: 44, background: 'rgba(13,148,136,0.25)' }}
      >
        <Bell size={22} style={{ color: 'var(--rv-teal)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <h3
          data-testid="announcement-title"
          className="text-lg font-semibold mb-1 break-words"
          style={{ fontFamily: 'Manrope,sans-serif', color: 'white' }}
        >
          {announcement.title}
        </h3>
        <p data-testid="announcement-message" className="text-sm whitespace-pre-line break-words" style={{ color: 'rgba(255,255,255,0.85)' }}>
          {announcement.message}
        </p>
      </div>
      <button
        data-testid="announcement-dismiss-btn"
        onClick={handleDismiss}
        className="flex-shrink-0 rounded-lg p-1.5 transition-colors"
        style={{ background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', color: 'white' }}
        aria-label="Закрыть уведомление"
        title="Понятно"
      >
        <XIcon size={18} />
      </button>
    </div>
  );
};


const Dashboard = ({ user, setCurrentView, showToast, selectCreateTask, openTaskChat, onShare }) => {
 // Notifications state
 const [dashboardNotifications, setDashboardNotifications] = React.useState({ unread: 0, unreadTaskId: null, priceChanges: [], executorApprovals: [] });
 const [showAllStories, setShowAllStories] = React.useState(false);
 // Адаптивность главной: новичок (0 заданий) видит мотивационный контент, активный — чистый дашборд
 const isNewUser = (user.total_tasks || 0) === 0;
 // Мини-статистика роста за неделю для виджета статистики
 const [weeklyStats, setWeeklyStats] = React.useState(null);
 React.useEffect(() => {
 axios.get(`${API}/stats/my-weekly`).then(r => setWeeklyStats(r.data)).catch(() => {});
 }, [user.id]);
 
 React.useEffect(() => {
 const fetchNotifications = async () => {
 try {
 const response = await axios.get(`${API}/my-discussions`);
 const discussions = response.data;
 const totalUnread = discussions.reduce((sum, t) =>sum + (t.unread_count || 0), 0);
 // Заказчик: ожидается оплата подтверждённой доплаты
 const pendingPriceChanges = discussions.filter(t => needsSurcharge(t, t.active_price_change) && t.customer_id === user.id);
 // Исполнитель: ожидается решение по предложенному изменению цены
 const pendingExecutorApprovals = discussions.filter(t =>t.active_price_change &&
 t.active_price_change.status === 'proposed' &&
 t.executor_id === user.id
 );
 setDashboardNotifications({ unread: totalUnread, unreadTaskId: (discussions.find(t => (t.unread_count || 0) > 0) || {}).id || null, priceChanges: pendingPriceChanges, executorApprovals: pendingExecutorApprovals });
 } catch (e) {
 // non-critical
 }
 };
 fetchNotifications();
 const interval = setInterval(fetchNotifications, 30000);
 return () =>clearInterval(interval);
 }, [user.id]);

 // 4. Хук для анимаций при прокрутке — one-shot reveal (без сброса, надёжно на мобильном)
 const [visibleSections, setVisibleSections] = React.useState({});

 React.useEffect(() => {
 const els = Array.from(document.querySelectorAll('[data-section]'));
 if (els.length === 0) return;

 const reveal = (name) => {
 if (!name) return;
 setVisibleSections(prev => (prev[name] ? prev : { ...prev, [name]: true }));
 };

 // Фолбэк без IntersectionObserver — показываем всё сразу
 if (typeof IntersectionObserver === 'undefined') {
 els.forEach(el =>reveal(el.dataset.section));
 return;
 }

 const observer = new IntersectionObserver((entries) => {
 entries.forEach((entry) => {
 if (entry.isIntersecting) {
 reveal(entry.target.dataset.section);
 observer.unobserve(entry.target); // анимируем один раз и оставляем видимым
 }
 });
 }, { threshold: 0.1, rootMargin: '0px 0px -8% 0px' });

 els.forEach(el =>observer.observe(el));

 // Страховка от «застрявших» блоков при быстрой прокрутке (особенно мобильный):
 // всё, что уже попало в область экрана, раскрываем принудительно
 const safety = setTimeout(() => {
 els.forEach(el => {
 const r = el.getBoundingClientRect();
 if (r.top < window.innerHeight && r.bottom > 0) reveal(el.dataset.section);
 });
 }, 500);

 return () => { observer.disconnect(); clearTimeout(safety); };
 }, []);
 
 return (
 <div className="space-y-8">
 <AnnouncementBanner />
 <style>{`
 @keyframes fadeInUp {
 from {
 opacity: 0;
 transform: translateY(30px);
 }
 to {
 opacity: 1;
 transform: translateY(0);
 }
 }
 
 @keyframes fadeInLeft {
 from {
 opacity: 0;
 transform: translateX(-30px);
 }
 to {
 opacity: 1;
 transform: translateX(0);
 }
 }
 
 @keyframes fadeInRight {
 from {
 opacity: 0;
 transform: translateX(30px);
 }
 to {
 opacity: 1;
 transform: translateX(0);
 }
 }
 
 @keyframes scaleIn {
 from {
 opacity: 0;
 transform: scale(0.9);
 }
 to {
 opacity: 1;
 transform: scale(1);
 }
 }
 
 .animate-on-scroll {
 opacity: 0;
 }
 
 .animate-on-scroll.visible {
 animation-duration: 0.8s;
 animation-fill-mode: both;
 animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
 }
 
 .animate-fade-up.visible {
 animation-name: fadeInUp;
 }
 
 .animate-fade-left.visible {
 animation-name: fadeInLeft;
 }
 
 .animate-fade-right.visible {
 animation-name: fadeInRight;
 }
 
 .animate-scale.visible {
 animation-name: scaleIn;
 }
 
 .delay-100 { animation-delay: 0.1s; }
 .delay-200 { animation-delay: 0.2s; }
 .delay-300 { animation-delay: 0.3s; }
 .delay-400 { animation-delay: 0.4s; }
 .delay-500 { animation-delay: 0.5s; }

 @keyframes heroScale {
   from { transform: scale(1.08); }
   to   { transform: scale(1); }
 }
 `}</style>
 <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 p-5 sm:p-6 bg-white rounded-2xl mb-8 border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.06)]" id="rv-profile-header">
 <AvatarUpload user={user} showToast={showToast} size={96} />
 <div className="flex-1 min-w-0">
 <div className="flex flex-wrap items-center gap-2 mb-2">
 <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold" style={{background: 'var(--rv-teal-light)', color: 'var(--rv-teal-dark)'}}>
 {user.role === "customer" ? "Заказчик" : user.role === "admin" ? "Администратор" : "Исполнитель"}
 </span>
 {user.role === "executor" && user.is_verified_executor && (
 <VerifiedExecutorBadge size="sm" showLabel={true} />
 )}
 </div>
 <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1.5 leading-tight tracking-tight">Добро пожаловать, {user.name}!
 </h1>
 <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
 {(user.role === "customer" || user.role === "admin")
 ? "Любые задания в любой точке мира — для себя и для бизнеса"
 : "Зарабатывайте, выполняя задания в вашем городе"
 }
 </p>
 </div>
 </div>



 {/* SEO Форк 2: тумблер согласия на публичный профиль (только исполнители) */}
 {user.role === "executor" && (
 <div id="rv-public-profile-toggle">
 <PublicProfileToggle user={user} showToast={showToast} />
 </div>
 )}

 {/* Чек-лист новичка: первые шаги (исполнители и заказчики) */}
 {(user.role === "executor" || user.role === "customer") && (
 <NewcomerChecklist user={user} setCurrentView={setCurrentView} isNewUser={isNewUser} />
 )}

 {/* Панель уведомлений на главной странице */}
 {(dashboardNotifications.unread > 0 || dashboardNotifications.priceChanges.length > 0 || dashboardNotifications.executorApprovals.length > 0) && (
 <div className="space-y-3 mb-6">
 {/* Уведомление о непрочитанных сообщениях */}
 {dashboardNotifications.unread > 0 && (
 <div
 data-testid="dashboard-unread-banner"
 className="flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:shadow-md"
 style={{backgroundColor: 'var(--reversia-teal)', color: 'white'}}
 onClick={() => {
 if (dashboardNotifications.unreadTaskId && openTaskChat) {
 openTaskChat(dashboardNotifications.unreadTaskId);
 } else {
 setCurrentView('discussions');
 }
 }}
 >
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
 <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
 <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"/>
 </svg>
 </div>
 <div>
 <p className="font-semibold text-sm">Новые сообщения</p>
 <p className="text-xs opacity-90">У вас {dashboardNotifications.unread} непрочитанных {dashboardNotifications.unread === 1 ? 'сообщение' : 'сообщений'} — нажмите, чтобы открыть чат
 </p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 <span className="px-2.5 py-1 rounded-full bg-white text-xs font-bold" style={{color: 'var(--reversia-teal)'}}>
 {dashboardNotifications.unread}
 </span>
 <svg className="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
 </svg>
 </div>
 </div>
 )}

 {/* Уведомление исполнителю: требуется ответ на предложение изменения цены */}
 {dashboardNotifications.executorApprovals.map(task => (
 <div
 key={task.id + '_exec'}
 data-testid="dashboard-price-approval-banner"
 className="flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:shadow-md border-l-4"
 style={{backgroundColor: '#F0F7FF', borderLeftColor: '#3B82F6'}}
 onClick={() => openTaskChat ? openTaskChat(task.id) : setCurrentView('discussions')}
 >
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#3B82F6', color: 'white'}}>
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd"/>
 </svg>
 </div>
 <div>
 <p className="font-semibold text-sm text-gray-900">Требуется ваш ответ</p>
 <p className="text-xs text-gray-600">По заданию "{task.title}": заказчик предлагает новую цену {task.active_price_change.new_price}₽ — нажмите, чтобы открыть чат задания
 </p>
 </div>
 </div>
 <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
 </svg>
 </div>
 ))}

 {/* Уведомление заказчику о требуемой доплате */}
 {dashboardNotifications.priceChanges.map(task => (
 <div
 key={task.id}
 data-testid="dashboard-surcharge-banner"
 className="flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:shadow-md border-l-4"
 style={{backgroundColor: '#FFF9F0', borderLeftColor: 'var(--reversia-gold)'}}
 onClick={() => openTaskChat ? openTaskChat(task.id) : setCurrentView('discussions')}
 >
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: 'var(--reversia-gold)', color: 'white'}}>
 <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
 <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"/>
 </svg>
 </div>
 <div>
 <p className="font-semibold text-sm text-gray-900">Требуется доплата</p>
 <p className="text-xs text-gray-600">Задание "{task.title}": доплата +{task.active_price_change.new_price - task.active_price_change.old_price}₽ — нажмите, чтобы открыть чат задания
 </p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 <span className="px-2 py-1 rounded-full text-xs font-bold text-white" style={{backgroundColor: 'var(--reversia-gold)'}}>Требует оплаты
 </span>
 <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
 </svg>
 </div>
 </div>
 ))}
 </div>
 )}

 {/* Эмоциональная секция с фото */}
 <div className="relative overflow-hidden rounded-2xl shadow-xl mb-8">
 <div 
 className="absolute inset-0 bg-cover bg-center"
 style={{
 backgroundImage: 'url(/images/main-hero-bg.png)',
 filter: 'brightness(0.7)',
 animation: 'heroScale 4s ease-out forwards',
 }}
 ></div>
 {/* Градиент снизу для лучшей читаемости текста */}
 <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
 <div className="relative bg-black bg-opacity-40 p-8 md:p-12">
 <div className="max-w-4xl mx-auto text-center text-white">
 <h3 className="text-3xl md:text-4xl font-bold mb-6 leading-tight"
 style={{color:'white', textShadow:'0 2px 12px rgba(0,0,0,0.7)', animation:'fadeInUp 0.8s ease-out 0.2s both'}}>
 {(user.role === "customer" || user.role === "admin")
 ? "Решите любую задачу в любом городе"
 : "Задания рядом — реальный заработок"
 }
 </h3>
 <p className="text-lg md:text-xl mb-8 leading-relaxed"
 style={{color:'rgba(255,255,255,0.95)', textShadow:'0 1px 8px rgba(0,0,0,0.6)', animation:'fadeInUp 0.8s ease-out 0.4s both'}}>
 {(user.role === "customer" || user.role === "admin")
 ? "Нужен мастер на даче, курьер в другом районе или специалист по SEO? Разместите задание — исполнитель откликнется именно там, где нужна работа."
 : "Ваши глаза и руки — чья-то помощь. Снять видео, привезти, починить, помочь по делу — каждое задание это возможность быть полезным и заработать на этом."
 }
 </p>
 <div className="flex flex-wrap justify-center gap-4" style={{animation:'fadeInUp 0.6s ease-out 0.5s both'}}>
 <button
 onClick={() => {
 if (user.role === "customer" || user.role === "admin") {
 selectCreateTask("general");
 setCurrentView("create");
 } else {
 setCurrentView("tasks");
 }
 }}
 className="group flex items-center space-x-2 text-sm font-medium bg-white/20 hover:bg-white/40 hover:scale-105 active:scale-95 backdrop-blur-sm border border-white/30 hover:border-white/60 rounded-full px-4 py-2 transition-all duration-300 cursor-pointer"
 >
 <Home size={16} className="transition-transform duration-300 group-hover:scale-110" />
 <span>Общие услуги</span>
 </button>
 <button
 onClick={() => {
 if (user.role === "customer" || user.role === "admin") {
 selectCreateTask("secret_shopper");
 setCurrentView("create");
 } else {
 setCurrentView("tasks");
 }
 }}
 className="group flex items-center space-x-2 text-sm font-medium bg-white/20 hover:bg-white/40 hover:scale-105 active:scale-95 backdrop-blur-sm border border-white/30 hover:border-white/60 rounded-full px-4 py-2 transition-all duration-300 cursor-pointer"
 >
 <Search size={16} className="transition-transform duration-300 group-hover:scale-110" />
 <span>Тайная проверка</span>
 </button>
 <button
 onClick={() => {
 if (user.role === "customer" || user.role === "admin") {
 selectCreateTask("mobile_journalist");
 setCurrentView("create");
 } else {
 setCurrentView("tasks");
 }
 }}
 className="group flex items-center space-x-2 text-sm font-medium bg-white/20 hover:bg-white/40 hover:scale-105 active:scale-95 backdrop-blur-sm border border-white/30 hover:border-white/60 rounded-full px-4 py-2 transition-all duration-300 cursor-pointer"
 >
 <Video size={16} className="transition-transform duration-300 group-hover:scale-110" />
 <span>Репортаж с места</span>
 </button>
 {(user.role === "customer" || user.role === "admin") && (
 <button
 onClick={() => { selectCreateTask("grave_care"); setCurrentView("create"); }}
 className="group flex items-center space-x-2 text-sm font-medium bg-white/20 hover:bg-white/40 hover:scale-105 active:scale-95 backdrop-blur-sm border border-white/30 hover:border-white/60 rounded-full px-4 py-2 transition-all duration-300 cursor-pointer"
 >
 <Flame size={16} className="transition-transform duration-300 group-hover:scale-110" />
 <span>Мемориальный сервис</span>
 </button>
 )}
 </div>
 </div>
 </div>
 </div>

 {/* Блок ценовых преимуществ (только новым заказчикам) */}
 {(user.role === "customer" || user.role === "admin") && isNewUser && (
 <div data-testid="dashboard-customer-price-card" className="relative p-8 rounded-2xl shadow-sm mb-8 overflow-hidden bg-white" style={{border: '1px solid var(--rv-border, #e2e8f0)'}}>
 <div className="relative">
 <div className="text-center mb-8">
 <div className="flex justify-center mb-3"><Wallet size={28} style={{color: 'var(--reversia-teal)'}} /></div>
 <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{color: 'var(--reversia-teal)'}}>Вы сами устанавливаете цену</h3>
 <p className="text-base text-gray-600 max-w-2xl mx-auto">Вы назначаете цену — исполнители сами решают, браться ли за задание. Не угадали с суммой? Не страшно: исполнитель предложит свои условия прямо в чате, а вы измените цену задания одним нажатием — ещё до начала работ.
 </p>
 </div>
 
 <div className="grid md:grid-cols-3 gap-3">
 <button type="button" data-testid="price-tile-photo" onClick={() => { selectCreateTask("general", "photo_video"); setCurrentView("create"); }} className="group bg-slate-50 hover:bg-white p-6 rounded-xl border border-slate-200 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md cursor-pointer text-center w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500">
 <div className="mb-3 flex justify-center"><Camera size={36} style={{color: 'var(--reversia-teal)'}} className="transition-transform duration-200 group-hover:scale-110" /></div>
 <h4 className="font-bold text-lg mb-2 text-slate-800">Фото дома детства</h4>
 <div className="text-3xl font-bold mb-1" style={{color: 'var(--reversia-teal)'}}>от 500 ₽</div>
 <p className="text-sm text-gray-500">Средняя цена заказчиков</p>
 </button>

            <button type="button" data-testid="price-tile-household" onClick={() => { selectCreateTask("general", "household"); setCurrentView("create"); }} className="group bg-slate-50 hover:bg-white p-6 rounded-xl border border-slate-200 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md cursor-pointer text-center w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500">
              <div className="mb-3 flex justify-center"><Home size={36} style={{color: 'var(--reversia-teal)'}} className="transition-transform duration-200 group-hover:scale-110" /></div>
              <h4 className="font-bold text-lg mb-2 text-slate-800">Помощь по дому</h4>
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--reversia-teal)'}}>от 1 500 ₽</div>
              <p className="text-sm text-gray-500">Уборка, покупки, мелкий ремонт</p>
            </button>

            <button type="button" data-testid="price-tile-professional" onClick={() => { selectCreateTask("general", ""); setCurrentView("create"); }} className="group bg-slate-50 hover:bg-white p-6 rounded-xl border border-slate-200 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md cursor-pointer text-center w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500">
              <div className="mb-3 flex justify-center"><Briefcase size={36} style={{color: 'var(--reversia-teal)'}} className="transition-transform duration-200 group-hover:scale-110" /></div>
              <h4 className="font-bold text-lg mb-2 text-slate-800">Профессиональные услуги</h4>
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--reversia-teal)'}}>от 2 000 ₽</div>
              <p className="text-sm text-gray-500">Ремонт, SEO, документы, консультации</p>
            </button>
 </div>
 
 <div className="text-center mt-8">
 <div className="flex flex-wrap justify-center gap-4 text-base font-medium mb-6 text-slate-700">
 <div className="flex items-center space-x-2">
 <Sparkles size={18} style={{color: 'var(--reversia-gold)'}} />
 <span>Справедливая цена</span>
 </div>
 <div className="flex items-center space-x-2">
 <ThumbsUp size={18} style={{color: 'var(--reversia-gold)'}} />
 <span>Довольный заказчик</span>
 </div>
 <div className="flex items-center space-x-2">
 <Star size={18} style={{color: 'var(--reversia-gold)'}} />
 <span>Качественный результат</span>
 </div>
 </div>
 <button
 onClick={() => { selectCreateTask("general"); setCurrentView("create"); }}
 className="reversia-btn-primary px-8 py-3"
 >Создать задание
 </button>
 </div>
 </div>
 </div>
 )}
 
 {/* Блок заработка для исполнителей (только новым) */}
 {user.role === "executor" && isNewUser && (
 <div data-testid="dashboard-executor-earnings-card" className="relative bg-white p-8 rounded-2xl shadow-sm mb-8 overflow-hidden" style={{border: '1px solid var(--rv-border, #e2e8f0)'}}>
 <div className="relative">
 <div className="text-center mb-8">
 <div className="flex justify-center mb-3"><Briefcase size={28} style={{color: 'var(--reversia-teal)'}} /></div>
 <h3 className="text-2xl sm:text-3xl font-bold mb-4" style={{color: 'var(--reversia-teal)'}}>Превратите прогулки в доход</h3>
 <p className="text-base text-gray-600">Зарабатывайте на том, что делаете каждый день
 </p>
 </div>
 
 <div className="grid md:grid-cols-3 gap-3">
 <div className="group bg-slate-50 hover:bg-white p-6 rounded-xl border border-slate-200 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md cursor-default">
 <div className="mb-3 flex justify-center"><Clock size={36} style={{color: 'var(--reversia-teal)'}} className="transition-transform duration-200 group-hover:scale-110" /></div>
 <h4 className="font-bold text-lg mb-2 text-slate-800">Среднее задание</h4>
 <div className="text-3xl font-bold mb-1" style={{color: 'var(--reversia-teal)'}}>1 200 ₽</div>
 <p className="text-sm text-gray-500">За 30 минут работы</p>
 </div>
 
 <div className="group bg-slate-50 hover:bg-white p-6 rounded-xl border border-slate-200 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md cursor-default">
 <div className="mb-3 flex justify-center"><CalendarDays size={36} style={{color: 'var(--reversia-teal)'}} className="transition-transform duration-200 group-hover:scale-110" /></div>
 <h4 className="font-bold text-lg mb-2 text-slate-800">Активный исполнитель</h4>
 <div className="text-3xl font-bold mb-1" style={{color: 'var(--reversia-teal)'}}>15 000 ₽</div>
 <p className="text-sm text-gray-500">В месяц (5–8 заданий/неделю)</p>
 </div>
 
 <div className="group bg-slate-50 hover:bg-white p-6 rounded-xl border border-slate-200 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md cursor-default">
 <div className="mb-3 flex justify-center"><Star size={36} style={{color: 'var(--reversia-teal)'}} className="transition-transform duration-200 group-hover:scale-110" /></div>
 <h4 className="font-bold text-lg mb-2 text-slate-800">Топ исполнители</h4>
 <div className="text-3xl font-bold mb-1" style={{color: 'var(--reversia-teal)'}}>35 000 ₽</div>
 <p className="text-sm text-gray-500">В месяц с высоким рейтингом</p>
 </div>
 </div>
 
 <div className="text-center mt-8">
 <div className="flex flex-wrap justify-center gap-4 text-base font-medium mb-6 text-slate-700">
 <div className="flex items-center space-x-2">
 <Zap size={18} style={{color: 'var(--reversia-gold)'}} />
 <span>Гибкий график</span>
 </div>
 <div className="flex items-center space-x-2">
 <Wallet size={18} style={{color: 'var(--reversia-gold)'}} />
 <span>Быстрые выплаты</span>
 </div>
 <div className="flex items-center space-x-2">
 <Sparkles size={18} style={{color: 'var(--reversia-gold)'}} />
 <span>Интересные задания</span>
 </div>
 </div>
 <button
 onClick={() =>setCurrentView("tasks")}
 className="reversia-btn-primary px-8 py-3"
 >Найти задание рядом
 </button>
 </div>
 </div>
 </div>
 )}

  {/* Блок комиссий для исполнителей */}
  {user.role === "executor" && (
    <CommissionDashboardSection />
  )}



 <div className="grid md:grid-cols-2 gap-6">
 <div
 data-testid="dashboard-nav-create"
 role="button"
 tabIndex={0}
 onClick={() =>setCurrentView((user.role === "customer" || user.role === "admin") ? "create" : "tasks")}
 onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCurrentView((user.role === "customer" || user.role === "admin") ? "create" : "tasks"); } }}
 className={`group cursor-pointer relative overflow-hidden rounded-2xl min-h-[320px] shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-transform duration-300 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(0,0,0,0.16)] animate-on-scroll animate-fade-left delay-100 ${visibleSections['grid-col-1'] ? 'visible' : ''}`}
 data-section="grid-col-1"
 >
 <div className="absolute inset-0 bg-cover transition-transform duration-500 group-hover:scale-105" style={{backgroundImage: 'url(/images/create-task-card.jpg)', backgroundPosition: 'center 42%'}} />
 <div className="absolute inset-0" style={{background: 'linear-gradient(to top, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.42) 50%, rgba(0,0,0,0.10) 100%)'}} />
 <div className="relative h-full flex flex-col justify-end p-6 min-h-[320px]">
 <h3 className="text-2xl font-bold text-white mb-1.5 leading-tight" style={{textShadow: '0 2px 10px rgba(0,0,0,0.5)'}}>
 {(user.role === "customer" || user.role === "admin") ? "Создать задание" : "Найти задания"}
 </h3>
 <p className="text-sm sm:text-base text-white/90 mb-4 max-w-sm leading-relaxed">
 {(user.role === "customer" || user.role === "admin")
 ? "Опишите, что нужно сделать, и найдите исполнителя рядом"
 : "Найдите задания рядом с вами и начните зарабатывать"
 }
 </p>
 <span className="inline-flex items-center gap-2 self-start px-5 py-2.5 rounded-xl text-sm font-semibold min-h-[44px] transition-colors group-hover:brightness-110" style={{background: 'var(--rv-teal)', color: 'white'}}>
 {(user.role === "customer" || user.role === "admin") ? "Создать задание" : "Найти задания"}
 <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
 </span>
 </div>
 </div>

 <div
 data-testid="dashboard-nav-mytasks"
 role="button"
 tabIndex={0}
 onClick={() =>setCurrentView("my-tasks")}
 onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCurrentView("my-tasks"); } }}
 className={`group cursor-pointer relative overflow-hidden rounded-2xl min-h-[320px] shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-transform duration-300 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(0,0,0,0.16)] animate-on-scroll animate-fade-right delay-300 ${visibleSections['grid-col-2'] ? 'visible' : ''}`}
 data-section="grid-col-2"
 >
 <div className="absolute inset-0 bg-cover transition-transform duration-500 group-hover:scale-105" style={{backgroundImage: 'url(/images/my-tasks-card.jpg)', backgroundPosition: 'center 42%'}} />
 <div className="absolute inset-0" style={{background: 'linear-gradient(to top, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.42) 50%, rgba(0,0,0,0.10) 100%)'}} />
 <div className="relative h-full flex flex-col justify-end p-6 min-h-[320px]">
 <h3 className="text-2xl font-bold text-white mb-1.5 leading-tight" style={{textShadow: '0 2px 10px rgba(0,0,0,0.5)'}}>Мои задания
 </h3>
 <p className="text-sm sm:text-base text-white/90 mb-4 max-w-sm leading-relaxed">Просмотрите свои текущие и завершённые задания
 </p>
 <span className="inline-flex items-center gap-2 self-start px-5 py-2.5 rounded-xl text-sm font-semibold min-h-[44px] transition-colors group-hover:brightness-110" style={{background: 'var(--rv-teal)', color: 'white'}}>Мои задания
 <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
 </span>
 </div>
 </div>
 </div>

 {/* Рассказать о Реверсии — мягкая бирюзовая карточка */}
 <div
 data-testid="dashboard-share-card"
 className="rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
 style={{background: 'var(--rv-teal-light)'}}
 >
 <div className="flex items-center gap-4 flex-1 min-w-0">
 <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{background: 'var(--rv-teal)'}}>
 <Share2 size={22} style={{color: 'white'}} />
 </div>
 <div className="min-w-0">
 <p className="text-base sm:text-lg font-semibold" style={{color: 'var(--rv-teal-dark)'}}>Знаете, кому пригодится Реверсия?</p>
 <p className="text-sm mt-0.5" style={{color: '#0f766e'}}>
 {user.role === "executor"
 ? "Знакомым пригодится подработка рядом с домом — отправьте им ссылку."
 : "Отправьте ссылку тем, кому нужна помощь с поручениями."}
 </p>
 </div>
 </div>
 <button
 data-testid="dashboard-share-btn"
 onClick={onShare}
 className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold flex-shrink-0 min-h-[44px] transition-colors"
 style={{background: 'var(--rv-teal)', color: 'white', cursor: 'pointer'}}
 onMouseEnter={e => e.currentTarget.style.background = 'var(--rv-teal-dark)'}
 onMouseLeave={e => e.currentTarget.style.background = 'var(--rv-teal)'}
 >Рассказать друзьям<ChevronRight size={16} />
 </button>
 </div>

 {/* Как это работает (только новым) */}
 {isNewUser && (
 <div data-testid="dashboard-how-it-works" className="relative overflow-hidden rounded-2xl shadow-sm mb-8 bg-white" style={{border: '1px solid var(--rv-border, #e2e8f0)'}}>
 <div className="relative p-8 md:p-12">
 <div className="max-w-6xl mx-auto">
 <div className={`text-center mb-12 animate-on-scroll animate-fade-up ${visibleSections['how-it-works-title'] ? 'visible' : ''}`} data-section="how-it-works-title">
 <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{color: 'var(--reversia-teal)'}}>Как это работает
 </h2>
 <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
 {(user.role === "customer" || user.role === "admin")
 ? "Местные исполнители возьмутся за дело, пока вы заняты своим"
 : "Три шага от свободной минуты до оплаты на карте"
 }
 </p>
 </div>
 
 <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
 <div className={`text-center animate-on-scroll animate-fade-left ${visibleSections['step-1'] ? 'visible' : ''}`} data-section="step-1">
 <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center text-2xl font-bold" style={{backgroundColor: 'var(--reversia-teal)', color: 'white'}}>
 1
 </div>
 {(user.role === "customer" || user.role === "admin") ? (
 <>
 <h3 className="text-xl font-bold text-slate-800 mb-4">Опишите задачу</h3>
 <p className="text-gray-600 leading-relaxed">Что нужно сделать и где: от фото локации до ремонта, покупок или проверки качества.
 <span className="block mt-2 font-medium" style={{color: 'var(--reversia-teal)'}}>Мы найдём того, кто рядом.</span>
 </p>
 </>
 ) : (
 <>
 <h3 className="text-xl font-bold text-slate-800 mb-4">Выберите задание рядом</h3>
 <p className="text-gray-600 leading-relaxed">Откройте список заданий в вашем городе: фото, доставка, проверки, помощь по делу.
 <span className="block mt-2 font-medium" style={{color: 'var(--reversia-teal)'}}>Берите то, что по пути.</span>
 </p>
 </>
 )}
 </div>
 
 <div className={`text-center animate-on-scroll animate-scale delay-100 ${visibleSections['step-2'] ? 'visible' : ''}`} data-section="step-2">
 <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center text-2xl font-bold" style={{backgroundColor: 'var(--reversia-teal)', color: 'white'}}>
 2
 </div>
 {(user.role === "customer" || user.role === "admin") ? (
 <>
 <h3 className="text-xl font-bold text-slate-800 mb-4">Найдётся исполнитель рядом</h3>
 <p className="text-gray-600 leading-relaxed">Кто-то поблизости возьмётся за ваше задание и согласует детали.
 <span className="block mt-2 font-medium" style={{color: 'var(--reversia-teal)'}}>Исполнитель рядом с вами.</span>
 </p>
 </>
 ) : (
 <>
 <h3 className="text-xl font-bold text-slate-800 mb-4">Выполните и отправьте отчёт</h3>
 <p className="text-gray-600 leading-relaxed">Согласуйте детали с заказчиком, выполните задание и приложите фото- или видеоотчёт.
 <span className="block mt-2 font-medium" style={{color: 'var(--reversia-teal)'}}>Всё общение — в чате платформы.</span>
 </p>
 </>
 )}
 </div>
 
 <div className={`text-center animate-on-scroll animate-fade-right delay-200 ${visibleSections['step-3'] ? 'visible' : ''}`} data-section="step-3">
 <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center text-2xl font-bold" style={{backgroundColor: 'var(--reversia-teal)', color: 'white'}}>
 3
 </div>
 {(user.role === "customer" || user.role === "admin") ? (
 <>
 <h3 className="text-xl font-bold text-slate-800 mb-4">Получите результат</h3>
 <p className="text-gray-600 leading-relaxed">Фото-отчёт, готовая работа или выполненная услуга.
 <span className="block mt-2 font-medium" style={{color: 'var(--reversia-teal)'}}>Оплата защищена эскроу.</span>
 </p>
 </>
 ) : (
 <>
 <h3 className="text-xl font-bold text-slate-800 mb-4">Получите оплату</h3>
 <p className="text-gray-600 leading-relaxed">Заказчик подтверждает работу — и деньги поступают вам. Средства зарезервированы в эскроу ещё до начала работы.
 <span className="block mt-2 font-medium" style={{color: 'var(--reversia-teal)'}}>Оплата гарантирована эскроу.</span>
 </p>
 </>
 )}
 </div>
 </div>
 
 <div className="text-center mt-12">
 <p className="text-gray-600 text-lg mb-6">
 {(user.role === "customer" || user.role === "admin")
 ? "Готовы поручить дело тому, кто рядом?"
 : "Готовы заработать на первом задании?"
 }
 </p>
 <button
 onClick={() =>setCurrentView((user.role === "customer" || user.role === "admin") ? "create" : "tasks")}
 className="reversia-btn-primary text-lg px-8 py-4"
 >
 {(user.role === "customer" || user.role === "admin") ? "Создать задание" : "Найти задания рядом"}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Истории наших клиентов (только новым) */}
 {isNewUser && (
 <div data-testid="dashboard-stories" className="mb-8">
 <div className="text-center mb-12">
 <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{color: 'var(--reversia-teal)'}}>Истории, которые меняют жизни
 </h2>
 <p className="text-lg text-gray-600 max-w-3xl mx-auto">
 {(user.role === "customer" || user.role === "admin")
 ? "Каждое задание — это чья-то решённая задача: тёплое воспоминание, спокойствие за близких или вовремя сделанное дело. Вот истории наших пользователей."
 : "За каждой из этих историй стоит исполнитель, который выполнил задание и заработал. Следующим можете быть вы."
 }
 </p>
 <div className="mt-5 flex justify-center"><ExecutorsProofBadge /></div>
 </div>

 <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 mb-8 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-6 md:overflow-visible">
 {/* История 1: Бабушка и могила */}
 <div className="relative bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden hover:shadow-[0_10px_24px_rgba(0,0,0,0.14)] transition-shadow duration-300 snap-start shrink-0 w-[85%] sm:w-[340px] md:w-auto">
 <div 
 className="h-48 bg-cover bg-center"
 style={{backgroundImage: 'url(/images/story-1.png)'}}
 ></div>
 <div className="p-6">
 <h3 className="font-bold text-lg mb-3" style={{color: 'var(--reversia-teal)'}}>
 "Я увидела дедушку снова"
 </h3>
 <p className="text-gray-600 text-sm leading-relaxed">
 <span className="font-medium">Мария Петровна, 78 лет:</span> "1000 км отделяли меня от могилы мужа. 
 Через Reversia я заказала уход за могилкой - исполнитель положил свежие цветы и убрал территорию. Прислал фото. 
 <span className="block mt-2 text-gray-800 font-medium">Сердце замерло — он не забыт."</span>
 </p>
 </div>
 </div>

 {/* История 2: Молодой человек с фото дома */}
 <div className="relative bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden hover:shadow-[0_10px_24px_rgba(0,0,0,0.14)] transition-shadow duration-300 snap-start shrink-0 w-[85%] sm:w-[340px] md:w-auto">
 <div 
 className="h-48 bg-cover bg-center"
 style={{backgroundImage: 'url(/images/story-2-warehouse.webp)'}}
 role="img" aria-label="Исполнитель со смартфоном проходит по пустому складу"
 ></div>
 <div className="p-6">
 <h3 className="font-bold text-lg mb-3" style={{color: 'var(--reversia-teal)'}}>
 "Сделка под контролем за 24 часа"
 </h3>
 <p className="text-gray-600 text-sm leading-relaxed">
 <span className="font-medium">Компания из Москвы:</span> покупали склад в Краснодаре. Лететь на осмотр — два дня и командировочные. Разместили задание на проверку объекта: исполнитель приехал на адрес, сделал 40 фотографий и видеообход, сверил состояние с заявленным и передал полный репортаж
 <span className="block mt-2 text-gray-800 font-medium">в тот же вечер. Сделка состоялась без единой командировки."</span>
 </p>
 </div>
 </div>

 {/* История 3: Пара с телефонами */}
 <div className="relative bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden hover:shadow-[0_10px_24px_rgba(0,0,0,0.14)] transition-shadow duration-300 snap-start shrink-0 w-[85%] sm:w-[340px] md:w-auto">
 <div 
 className="h-48 bg-cover bg-center"
 style={{backgroundImage: 'url(/images/story-3.png)'}}
 ></div>
 <div className="p-6">
 <h3 className="font-bold text-lg mb-3" style={{color: 'var(--reversia-teal)'}}>
 "Решение принято!"
 </h3>
 <p className="text-gray-600 text-sm leading-relaxed">
 <span className="font-medium">Семья Ким:</span> "Покупали квартиру в другом городе. Заказали 20 фото района, 
 школы, магазинов. Увидели всё как есть — без прикрас. 
 <span className="block mt-2 text-gray-800 font-medium">Купили и не пожалели."</span>
 </p>
 </div>
 </div>

 {/* Истории 4-6: скрыты за кнопкой */}
 {showAllStories && (
 <>
 {/* История: бытовая помощь — ремонт */}
 <div className="relative bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden hover:shadow-[0_10px_24px_rgba(0,0,0,0.14)] transition-shadow duration-300 snap-start shrink-0 w-[85%] sm:w-[340px] md:w-auto">
 <div 
 className="h-48 bg-cover bg-center"
 style={{backgroundImage: 'url(/images/story-plumber.jpg)'}}
 ></div>
 <div className="p-6">
 <h3 className="font-bold text-lg mb-3" style={{color: 'var(--reversia-teal)'}}>
 "Помог, пока я был в отъезде"
 </h3>
 <p className="text-gray-600 text-sm leading-relaxed">
 <span className="font-medium">Сергей, в командировке:</span> "Дома прорвало кран, а я за тысячу километров. Через Reversia нашёл человека: приехал, перекрыл воду, дождался мастера. Прислал фото —
 <span className="block mt-2 text-gray-800 font-medium">всё под контролем."</span>
 </p>
 </div>
 </div>

 {/* История: бытовая помощь — сборка мебели */}
 <div className="relative bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden hover:shadow-[0_10px_24px_rgba(0,0,0,0.14)] transition-shadow duration-300 snap-start shrink-0 w-[85%] sm:w-[340px] md:w-auto">
 <div 
 className="h-48 bg-cover bg-center"
 style={{backgroundImage: 'url(/images/story-wardrobe.jpg)'}}
 ></div>
 <div className="p-6">
 <h3 className="font-bold text-lg mb-3" style={{color: 'var(--reversia-teal)'}}>
 "Шкаф собрали за вечер"
 </h3>
 <p className="text-gray-600 text-sm leading-relaxed">
 <span className="font-medium">Ольга, Краснодар:</span> "Купила шкаф, а инструкция — как ребус. Оставила задание на Reversia, вечером пришёл исполнитель и собрал за час. Быстро, недорого —
 <span className="block mt-2 text-gray-800 font-medium">и без нервов."</span>
 </p>
 </div>
 </div>

 {/* История 4: Исследователь */}
 <div className="relative bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden hover:shadow-[0_10px_24px_rgba(0,0,0,0.14)] transition-shadow duration-300 snap-start shrink-0 w-[85%] sm:w-[340px] md:w-auto">
 <div 
 className="h-48 bg-cover bg-center"
 style={{backgroundImage: 'url(/images/story-4.png)'}}
 ></div>
 <div className="p-6">
 <h3 className="font-bold text-lg mb-3" style={{color: 'var(--reversia-teal)'}}>
 "Тайна раскрыта"
 </h3>
 <p className="text-gray-600 text-sm leading-relaxed">
 <span className="font-medium">Дмитрий, исследователь:</span> "Странные круги на карте Google Earth мучили года. 
 Нашёл местного жителя через Reversia — оказалось древнее капище. 
 <span className="block mt-2 text-gray-800 font-medium">История открыла секрет."</span>
 </p>
 </div>
 </div>

 {/* История 5: Выпускники */}
 <div className="relative bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden hover:shadow-[0_10px_24px_rgba(0,0,0,0.14)] transition-shadow duration-300 snap-start shrink-0 w-[85%] sm:w-[340px] md:w-auto">
 <div 
 className="h-48 bg-cover bg-center"
 style={{backgroundImage: 'url(/images/story-5.png)'}}
 ></div>
 <div className="p-6">
 <h3 className="font-bold text-lg mb-3" style={{color: 'var(--reversia-teal)'}}>
 "Встреча через 20 лет"
 </h3>
 <p className="text-gray-600 text-sm leading-relaxed">
 <span className="font-medium">Одноклассники, выпуск 2003:</span> "Планировали встречу, но все разбросаны по миру. 
 Заказали фото школы, класса, столовой. Рассматривали вместе по видеосвязи — 
 <span className="block mt-2 text-gray-800 font-medium">как будто встретились там."</span>
 </p>
 </div>
 </div>

 {/* История 6: Видео-экскурсии */}
 <div className="relative bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden hover:shadow-[0_10px_24px_rgba(0,0,0,0.14)] transition-shadow duration-300 snap-start shrink-0 w-[85%] sm:w-[340px] md:w-auto">
 <div 
 className="h-48 bg-cover bg-center"
 style={{backgroundImage: 'url(/images/story-6.png)'}}
 ></div>
 <div className="p-6">
 <h3 className="font-bold text-lg mb-3" style={{color: 'var(--reversia-teal)'}}>
 "Путешествие не выходя из дома"
 </h3>
 <p className="text-gray-600 text-sm leading-relaxed">
 <span className="font-medium">Анна, экскурсовод из Санкт-Петербурга:</span> "Создала персональную 25-минутную видео-экскурсию по Дворцовой площади для школьников из Новосибирска. 
 Рассказала историю каждого здания с профессиональными комментариями — 
 <span className="block mt-2 text-gray-800 font-medium">как живая экскурсия в записи."</span>
 </p>
 </div>
 </div>
 </>
 )}
 </div>

 {/* Кнопка раскрытия историй */}
 <div className="text-center mb-8">
 <button
 onClick={() =>setShowAllStories(prev => !prev)}
 className="inline-flex items-center gap-2 px-6 py-3 border-2 rounded-xl font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
 style={{borderColor: 'var(--reversia-teal)', color: 'var(--reversia-teal)', background: 'transparent'}}
 >
 {showAllStories ? (
 <>
 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7"/></svg>Скрыть истории
 </>
 ) : (
 <>
 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>Читать больше историй
 </>
 )}
 </button>
 </div>

 <div className="text-center">
 <div className="reversia-card inline-block p-8">
 <h3 className="text-xl font-bold mb-4" style={{color: 'var(--reversia-teal)'}}>Готовы написать свою историю?
 </h3>
 <p className="text-gray-600 mb-6 max-w-md">
 {(user.role === "customer" || user.role === "admin")
 ? "На Реверсии каждый день решают самые разные задачи: от бытовых дел до тёплой весточки из родных мест. Ваша история может стать следующей."
 : "Исполнители уже зарабатывают, выполняя простые задания рядом с домом. Выберите первое задание сегодня."
 }
 </p>
 <button
 onClick={() =>setCurrentView((user.role === "customer" || user.role === "admin") ? "create" : "tasks")}
 className="reversia-btn-primary px-8 py-4"
 >
 {(user.role === "customer" || user.role === "admin") ? " Создать свою историю" : " Помочь создать историю"}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Виджет: статистика + приоритетный доступ (перед отзывами) */}
 {(() => {
 const rating = user.rating || 0;
 const reviews = user.total_reviews || 0;
 const TARGET = 4.5;
 const hasAccess = rating >= TARGET;
 let reviewsNeeded = 0;
 if (!hasAccess) {
 if (reviews === 0) {
 reviewsNeeded = null;
 } else {
 const currentSum = rating * reviews;
 const needed = Math.ceil(2 * (TARGET * reviews - currentSum));
 reviewsNeeded = Math.max(needed, 1);
 }
 }
 const progressPct = Math.min((rating / 5) * 100, 100);
 const markerPct = (TARGET / 5) * 100;
 const isExec = user.role === 'executor';
 const history = (weeklyStats && Array.isArray(weeklyStats.history)) ? weeklyStats.history : null;
 const ratingHistory = (weeklyStats && Array.isArray(weeklyStats.rating_history)) ? weeklyStats.rating_history : null;
 const buildSparkline = (data, stroke, fill, forcedMax) => {
 if (!data || data.length < 2 || !data.some(v => v > 0)) return null;
 const w = 128, h = 34, pad = 3;
 const max = forcedMax || Math.max(...data, 1);
 const stepX = (w - pad * 2) / (data.length - 1);
 const pts = data.map((v, i) => [pad + i * stepX, h - pad - (Math.min(v, max) / max) * (h - pad * 2)]);
 const line = pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
 const area = `M ${pts[0][0].toFixed(1)},${(h - pad).toFixed(1)} L ` + pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' L ') + ` L ${pts[pts.length - 1][0].toFixed(1)},${(h - pad).toFixed(1)} Z`;
 const last = pts[pts.length - 1];
 return (
 <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
 <path d={area} fill={fill} />
 <polyline points={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
 <circle cx={last[0]} cy={last[1]} r="3" fill={stroke} />
 </svg>
 );
 };
 const taskSpark = buildSparkline(history, 'var(--reversia-teal)', 'rgba(13,148,136,0.12)');
 const ratingSpark = buildSparkline(ratingHistory, 'var(--reversia-gold)', 'rgba(245,158,11,0.14)', 5);
 return (
 <div data-testid="dashboard-stats-widget" className="reversia-card mb-8">
 <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Ваша статистика</h3>
 {/* Метрики */}
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div className="rounded-2xl p-4 sm:p-5" style={{background: '#f0fdfa', border: '1px solid var(--rv-border)'}}>
 <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
 <div className="text-3xl sm:text-4xl font-bold" style={{color: 'var(--reversia-teal)'}}><CountUp value={user.total_tasks} /></div>
 {weeklyStats && weeklyStats.this_week > 0 && (
 <span data-testid="weekly-growth-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{background: 'rgba(13,148,136,0.12)', color: 'var(--reversia-teal)'}}>
 <TrendingUp size={12} /> +{weeklyStats.this_week} за неделю
 </span>
 )}
 </div>
 <div className="text-sm text-gray-500 mt-1">{isExec ? 'Выполненных заданий' : 'Созданных заданий'}</div>
 {taskSpark && (
 <div className="mt-3" data-testid="weekly-sparkline">
 {taskSpark}
 <div className="text-xs text-gray-500 mt-1">Динамика за 4 недели</div>
 </div>
 )}
 </div>
 <div className="rounded-2xl p-4 sm:p-5" style={{background: '#fffbeb', border: '1px solid var(--rv-border)'}}>
 <div className="text-3xl sm:text-4xl font-bold" style={{color: 'var(--reversia-gold)'}}><CountUp value={rating} decimals={1} /></div>
 <div className="text-sm text-gray-500 mt-1">
 Рейтинг · из 5.0{reviews > 0 ? ` · ${reviews} ${reviews === 1 ? 'отзыв' : reviews < 5 ? 'отзыва' : 'отзывов'}` : ''}
 </div>
 <div className="flex gap-0.5 mt-3">
 {[1, 2, 3, 4, 5].map(i => (
 <Star key={i} size={16} style={{color: i <= Math.round(rating) ? 'var(--reversia-gold)' : '#e5e7eb'}} fill={i <= Math.round(rating) ? 'var(--reversia-gold)' : '#e5e7eb'} />
 ))}
 </div>
 {ratingSpark && (
 <div className="mt-3" data-testid="rating-sparkline">
 {ratingSpark}
 <div className="text-xs text-gray-500 mt-1">Динамика рейтинга</div>
 </div>
 )}
 </div>
 </div>

 {/* Приоритетный доступ — только исполнители */}
 {isExec && (
 <div
 data-testid="priority-access-block"
 className="mt-5 rounded-xl p-4 overflow-hidden"
 style={{
 border: hasAccess ? '1.5px solid #0d9488' : '1.5px solid #d97706',
 background: hasAccess
 ? 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)'
 : 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'
 }}
 >
 <div className="flex items-start gap-3 mb-4">
 <div
 className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
 style={{background: hasAccess ? '#0d9488' : '#d97706'}}
 >
 {hasAccess ? (
 <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
 </svg>
 ) : (
 <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
 <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
 </svg>
 )}
 </div>
 <div className="flex-1">
 <div className="font-semibold text-sm" style={{color: hasAccess ? '#0d9488' : '#92400e'}}>
 {hasAccess ? 'Приоритетный доступ активен' : 'Приоритетный доступ заблокирован'}
 </div>
 <div className="text-xs mt-0.5" style={{color: hasAccess ? '#134e4a' : '#78350f'}}>
 {hasAccess
 ? 'Вы видите новые задания сразу после размещения заказчиком'
 : 'Исполнители с рейтингом 4.5+ получают задания на 30 мин раньше'}
 </div>
 </div>
 </div>

 {/* Прогресс-бар рейтинга */}
 <div className="mb-3">
 <div className="flex justify-between text-xs mb-1" style={{color: hasAccess ? '#0f766e' : '#92400e'}}>
 <span>Ваш рейтинг: <strong>{rating.toFixed(1)}</strong></span>
 <span>Цель: <strong>{TARGET}</strong> — приоритетный доступ</span>
 </div>
 <div className="relative h-3 bg-white rounded-full overflow-visible" style={{border: '1px solid #e5e7eb'}}>
 <div
 className="absolute top-0 left-0 h-full rounded-full transition-all duration-700"
 style={{
 width: `${progressPct}%`,
 background: hasAccess
 ? 'linear-gradient(90deg, #0d9488, #14b8a6)'
 : 'linear-gradient(90deg, #f59e0b, #fbbf24)'
 }}
 />
 <div
 className="absolute top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-full"
 style={{left: `${markerPct}%`, background: hasAccess ? '#0d9488' : '#b45309'}}
 />
 </div>
 <div className="relative h-3 mt-0.5">
 <div
 className="absolute text-xs font-semibold -translate-x-1/2"
 style={{left: `${markerPct}%`, color: hasAccess ? '#0d9488' : '#b45309', top: 0}}
 >
 4.5
 </div>
 </div>
 </div>

 {/* Подсказка сколько отзывов нужно */}
 {!hasAccess && (
 <div className="mt-2 text-xs rounded-md px-3 py-2" style={{background: 'rgba(217,119,6,0.12)', color: '#92400e'}}>
 {reviewsNeeded === null
 ? 'Выполните первое задание и получите оценку 5 — и приоритетный доступ станет ближе'
 : `Ещё ${reviewsNeeded} ${reviewsNeeded === 1 ? 'оценка' : reviewsNeeded < 5 ? 'оценки' : 'оценок'} на 5 — и ваш рейтинг достигнет 4.5`}
 </div>
 )}
 </div>
 )}
 </div>
 );
 })()}

 {/* User Reviews Section */}
 <UserProfileReviewsSection user={user} />

 {/* App Reviews Section */}
 <AppReviewsSection />

 {/* Footer with Legal Information */}
 <div className="mt-12 pt-8 border-t border-gray-200">
 <div className="reversia-card">
 <h3 className="text-lg font-semibold mb-6 text-center" style={{color: 'var(--reversia-teal)'}}>Информация</h3>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
 
 {/* Колонка 1: Платформа */}
 <div>
 <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Платформа</h4>
 <div className="space-y-2">
 <button onClick={() =>setCurrentView("help")} className="block w-full text-left text-sm text-gray-600 hover:text-teal-600 transition-colors py-1">Помощь <span className="block text-xs text-gray-400">Инструкции и FAQ</span>
 </button>
 <a href="/legal/rules" data-testid="dash-footer-rules" className="block w-full text-left text-sm text-gray-600 hover:text-teal-600 transition-colors py-1">Правила платформы <span className="block text-xs text-gray-400">Платежи и качество</span>
 </a>
 <a href="/legal/contacts" data-testid="dash-footer-contacts" className="block w-full text-left text-sm text-gray-600 hover:text-teal-600 transition-colors py-1">Контакты <span className="block text-xs text-gray-400">Связь с администрацией</span>
 </a>
 </div>
 </div>

 {/* Колонка 2: Компания */}
 <div>
 <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Компания</h4>
 <div className="space-y-2">
 <a href="/legal/requisites" data-testid="dash-footer-requisites" className="block w-full text-left text-sm text-gray-600 hover:text-teal-600 transition-colors py-1">О компании <span className="block text-xs text-gray-400">Реквизиты и информация</span>
 </a>
 <a href="/legal/offer" data-testid="dash-footer-offer" className="block w-full text-left text-sm text-gray-600 hover:text-teal-600 transition-colors py-1">Публичная оферта <span className="block text-xs text-gray-400">Основные условия</span>
 </a>
 <a href="/legal/privacy" data-testid="dash-footer-privacy" className="block w-full text-left text-sm text-gray-600 hover:text-teal-600 transition-colors py-1">Конфиденциальность <span className="block text-xs text-gray-400">Политика данных</span>
 </a>
 </div>
 </div>

 {/* Колонка 3: Для исполнителей */}
 <div>
 <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Для исполнителей</h4>
 <div className="space-y-2">
 <a href="/legal/performer" data-testid="dash-footer-performer" className="block w-full text-left text-sm text-gray-600 hover:text-teal-600 transition-colors py-1">Соглашение исполнителя <span className="block text-xs text-gray-400">Для самозанятых</span>
 </a>
 <a href="/legal/refund" data-testid="dash-footer-refund" className="block w-full text-left text-sm text-gray-600 hover:text-teal-600 transition-colors py-1">Возврат и обмен <span className="block text-xs text-gray-400">Условия возврата</span>
 </a>
 </div>
 </div>

 </div>
 
 <div className="mt-8 pt-4 border-t border-gray-200 text-center text-sm text-gray-500">
   <div className="flex justify-center gap-5 mb-4">
     <a href="https://vk.com/club239397750" target="_blank" rel="noopener noreferrer" aria-label="ВКонтакте"
       style={{color:'var(--rv-text-muted)', transition:'color 0.2s'}}
       onMouseEnter={e => e.currentTarget.style.color='var(--rv-teal)'}
       onMouseLeave={e => e.currentTarget.style.color='var(--rv-text-muted)'}
     >
       <i className="fa-brands fa-vk" style={{fontSize:'1.4rem'}}></i>
     </a>
     <a href="https://t.me/reversia_ru" target="_blank" rel="noopener noreferrer" aria-label="Telegram"
       style={{color:'var(--rv-text-muted)', transition:'color 0.2s'}}
       onMouseEnter={e => e.currentTarget.style.color='var(--rv-teal)'}
       onMouseLeave={e => e.currentTarget.style.color='var(--rv-text-muted)'}
     >
       <i className="fa-brands fa-telegram" style={{fontSize:'1.4rem'}}></i>
     </a>
     <a href="https://instagram.com/reversia.ru" target="_blank" rel="noopener noreferrer" aria-label="Instagram"
       style={{color:'var(--rv-text-muted)', transition:'color 0.2s'}}
       onMouseEnter={e => e.currentTarget.style.color='var(--rv-teal)'}
       onMouseLeave={e => e.currentTarget.style.color='var(--rv-text-muted)'}
     >
       <i className="fa-brands fa-instagram" style={{fontSize:'1.4rem'}}></i>
     </a>
     <a href="https://www.tiktok.com/@reversia_ru" target="_blank" rel="noopener noreferrer" aria-label="TikTok"
       style={{color:'var(--rv-text-muted)', transition:'color 0.2s'}}
       onMouseEnter={e => e.currentTarget.style.color='var(--rv-teal)'}
       onMouseLeave={e => e.currentTarget.style.color='var(--rv-text-muted)'}
     >
       <i className="fa-brands fa-tiktok" style={{fontSize:'1.4rem'}}></i>
     </a>
   </div>
   <p className="mb-2">Поддержка: <a href="mailto:support@reversia.ru" style={{color:'var(--rv-teal)'}}>support@reversia.ru</a> · Сотрудничество: <a href="mailto:promo@reversia.ru" style={{color:'var(--rv-teal)'}}>promo@reversia.ru</a> · Telegram: <a href="https://t.me/reversia_ru" target="_blank" rel="noopener noreferrer" style={{color:'var(--rv-teal)'}}>@reversia_ru</a> · <a href="/legal/sitemap" style={{color:'var(--rv-teal)'}} data-testid="dash-footer-sitemap">Карта сайта</a></p>
   <p>© {new Date().getFullYear()} Реверсия. Все права защищены.</p>
   <p className="mt-1">ИНН: 230808284051</p>
 </div>
 </div>
 </div>
 </div>
 );
};

// Discussions List Component
// Task List Component
// Task Map Component
// Journalist Verification Component
// Journalist Section Component
// Payment Cards Section Component
// Create Task Component
// Checklist Component for Grave Care
// Report Form Component for Secret Shopper
// My Disputes Component
// My Tasks Component
// My Tasks Component - SIMPLIFIED TO FIX CRITICAL ERROR
// Metadata indicator component for mobile correspondent files
// Task Results Viewer Component
// Task Chat Only Component (for discussion modal)
// Enhanced Task Chat with Sound Notifications
// Main Task Details Component
// Компонент загрузки доработки для исполнителя
export const TaskDetails = ({ task, user, onBack, onTaskUpdate, showToast, uploadFiles, setUploadFiles, uploadFileNames = [], setUploadFileNames = () => {} }) => {
 // Safety checks
 if (!task || !user) {
 return (
 <div className="flex items-center justify-center min-h-screen">
 <div className="text-center">
 <h2 className="text-xl font-semibold text-red-600 mb-2">Ошибка загрузки задания</h2>
 <p className="text-gray-600 mb-4">Задание не найдено или данные повреждены</p>
 <button
 onClick={onBack}
 className="reversia-btn reversia-btn-primary reversia-btn-md"
 >
 ← Вернуться к заданиям
 </button>
 </div>
 </div>
 );
 }

 // Helper function to get API URL
 const getApiUrl = () => {
 const API = import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
 if (!API) {
 throw new Error('API URL not configured');
 }
 return API;
 };

 const [messages, setMessages] = useState([]);
 const [newMessage, setNewMessage] = useState("");
 const [loading, setLoading] = useState(true);
 // uploadFiles теперь приходит из родительского компонента
 const [fileProcessing, setFileProcessing] = useState(false);
 const [revisionFiles, setRevisionFiles] = useState([]); // Файлы доработки — хранятся в TaskDetails
 const [renderKey, setRenderKey] = useState(0); // Для принудительного перерендера
 const [completionLoading, setCompletionLoading] = useState(false);
 const [showPriceChange, setShowPriceChange] = useState(false);
 const [priceChangeData, setPriceChangeData] = useState({ 
 newPrice: task?.price || 0, 
 reason: "" 
 });
 const [activePriceChange, setActivePriceChange] = useState(null);
 const [creatingPayment, setCreatingPayment] = useState(false);
 const [currentTask, setCurrentTask] = useState(task); // Track current task state
 const [chatCollapsed, setChatCollapsed] = useState(true);
 const [showComplaintModalChat, setShowComplaintModalChat] = useState(false);
 const [complaintTextChat, setComplaintTextChat] = useState("");
 const [complaintSubmittingChat, setComplaintSubmittingChat] = useState(false);
 const [complaintSentChat, setComplaintSentChat] = useState(false);
 const [uploadFilesCollapsed, setUploadFilesCollapsed] = useState(false);
 const [resultText, setResultText] = useState("");
 const [fileInputKey, setFileInputKey] = useState(0);
 const [uploadedRefs, setUploadedRefs] = useState([]);
 const [largeThumbnails, setLargeThumbnails] = useState({});
 const [uploadProgressItems, setUploadProgressItems] = useState([]);
 const [uploadOverallPercent, setUploadOverallPercent] = useState(0);
 const uploadAbortRef = useRef(null);
 const [resultsCollapsed, setResultsCollapsed] = useState(true);
 const [confirmingTask, setConfirmingTask] = useState(false);
 const [showDisputeSection, setShowDisputeSection] = useState(false);
 const [disputeFormDetails, setDisputeFormDetails] = useState({ reason: '', description: '' });
 const [disputeSubmitting, setDisputeSubmitting] = useState(false);
 const [showOpenDisputeSection, setShowOpenDisputeSection] = useState(false);
 const [openDisputeFormDetails, setOpenDisputeFormDetails] = useState({ reason: '', description: '' });
 const [openDisputeSubmitting, setOpenDisputeSubmitting] = useState(false);
 const [openDisputeEvidence, setOpenDisputeEvidence] = useState([]);
 const [showRevisionSection, setShowRevisionSection] = useState(false);
 const [revisionComment, setRevisionComment] = useState('');
 const [revisionSubmitting, setRevisionSubmitting] = useState(false);

 // Профиль собеседника для заголовка чата
 const [chatOtherProfile, setChatOtherProfile] = useState(null);
 const [chatAvatarModal, setChatAvatarModal] = useState(null);
 const [journalistCardOpen, setJournalistCardOpen] = useState(false);
 const chatOtherUserId = String(task.customer_id) === String(user.id)
 ? (task.discussing_executor_id || task.executor_id)
 : task.customer_id;
 const chatOtherName = String(task.customer_id) === String(user.id)
 ? (task.executor_name || 'Исполнитель')
 : (task.customer_name || 'Заказчик');
 const chatOtherRole = String(task.customer_id) === String(user.id) ? 'исполнитель' : 'заказчик';

 useEffect(() => {
 if (chatOtherUserId) {
 const API = process.env.REACT_APP_BACKEND_URL;
 axios.get(`${API}/api/users/${chatOtherUserId}/public-profile`)
 .then(r =>setChatOtherProfile(r.data))
 .catch(() => {});
 }
 }, [chatOtherUserId]);

 // Таймер обратного отсчёта 48 часов для блока подтверждения
 const [confirmCountdown, setConfirmCountdown] = useState(null);

 useEffect(() => {
 if (currentTask.status !== 'completed' || (!currentTask.completed_at && !currentTask.updated_at)) return;

 const DEADLINE_MS = 48 * 60 * 60 * 1000; // 48 часов
 const completedAt = new Date(currentTask.completed_at || currentTask.updated_at).getTime();

 const tick = () => {
 const remaining = completedAt + DEADLINE_MS - Date.now();
 setConfirmCountdown(remaining);
 };
 tick();
 const interval = setInterval(tick, 1000);
 return () =>clearInterval(interval);
 }, [currentTask.status, currentTask.completed_at, currentTask.updated_at]);

 // Данные отчёта тайного покупателя — хранятся в localStorage чтобы не сбрасываться
 // при выборе файлов из галереи на мобильном (браузер выгружает страницу из памяти)
 const REPORT_DRAFT_KEY = `report_draft_${task.id}`;
 const [secretShopperReportData, setSecretShopperReportData] = useState(() => {
 try {
 const saved = localStorage.getItem(`report_draft_${task.id}`);
 return saved ? JSON.parse(saved) : {};
 } catch { return {}; }
 });
 const handleReportChange = (data) => {
 setSecretShopperReportData(data);
 try { localStorage.setItem(REPORT_DRAFT_KEY, JSON.stringify(data)); } catch {}
 };
 const clearReportDraft = () => {
 try { localStorage.removeItem(REPORT_DRAFT_KEY); } catch {}
 };
 const [externalLinks, setExternalLinks] = useState(() => {
 // Load from localStorage or empty array
 if (!task?.id) return [];
 const saved = localStorage.getItem(`external_links_${task.id}`);
 return saved ? JSON.parse(saved) : [];
 });
 const [showLinkModal, setShowLinkModal] = useState(false);
 const [linkUrl, setLinkUrl] = useState('');
 const [linkTitle, setLinkTitle] = useState('');
 const [referenceImageModal, setReferenceImageModal] = useState(null); // Для модального окна подсказок
 const chatEndRef = useRef(null); // Reference for auto-scroll

 useEffect(() => {
 console.log(' TaskDetails useEffect started');
 
 fetchMessages();
 fetchActivePriceChange();
 // ВРЕМЕННО ОТКЛЮЧАЕМ fetchUpdatedTask - он сбрасывает externalLinks
 // fetchUpdatedTask();
 
 // Mark messages as read when opening chat
 const markMessagesAsRead = async () => {
 try {
 const API = import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
 if (!API) {
 console.warn('No API URL for marking messages as read');
 return;
 }
 await axios.post(`${API}/api/tasks/${task.id}/messages/mark-read`);
 console.log('Messages marked as read');
 } catch (error) {
 console.error('Error marking messages as read:', error);
 // Don't show error to user, it's not critical
 }
 };
 
 markMessagesAsRead();
 
 const interval = setInterval(() => {
 fetchMessages();
 fetchActivePriceChange();
 // ВРЕМЕННО ОТКЛЮЧАЕМ интервальное обновление задачи
 // fetchUpdatedTask();
 }, 3000);
 
 return () =>clearInterval(interval);
 }, []);

 // Use refs to track previous values
 const prevMessagesLength = useRef(0);
 const prevActivePriceChange = useRef(null);

 // Check if user is at the bottom of the chat
 const isUserAtBottom = () => {
 const chatContainer = chatEndRef.current?.parentElement;
 if (!chatContainer) return true;
 
 const { scrollTop, scrollHeight, clientHeight } = chatContainer;
 // Consider user "at bottom" if within 100px of the bottom
 return (scrollHeight - scrollTop - clientHeight) < 100;
 };

 // Auto-scroll to bottom only when NEW messages arrive AND user is already at bottom
 useEffect(() => {
 if (messages.length > 0) {
 const isInitialLoad = prevMessagesLength.current === 0;
 const hasNewMessages = messages.length >prevMessagesLength.current;
 
 if (isInitialLoad || (hasNewMessages && isUserAtBottom())) {
 setTimeout(() => {
 scrollToBottom();
 }, 200);
 }
 prevMessagesLength.current = messages.length;
 prevActivePriceChange.current = activePriceChange;
 }
 }, [messages, activePriceChange]);

 // Скролл к последнему сообщению при раскрытии чата
 useEffect(() => {
 if (!chatCollapsed && messages.length > 0) {
 setTimeout(() =>scrollToBottom(), 300);
 }
 }, [chatCollapsed]);

 const scrollToBottom = () => {
 // Simplified scroll for mobile compatibility
 setTimeout(() => {
 if (chatEndRef.current) {
 // Force scroll to bottom on mobile
 const chatContainer = chatEndRef.current.parentElement;
 if (chatContainer) {
 chatContainer.scrollTop = chatContainer.scrollHeight;
 }
 // Also try the standard method
 chatEndRef.current.scrollIntoView({ behavior: "auto", block: "end" });
 }
 }, 100);
 };

 // Fetch updated task data to get new price
 const fetchUpdatedTask = async () => {
 try {
 const response = await axios.get(`${API}/tasks/my`);
 const list = Array.isArray(response.data) ? response.data : (response.data?.items || response.data?.tasks || []);
 const updatedTask = list.find(t =>t.id === task.id);
 if (updatedTask) {
 // Preserve external links when updating task
 const currentExternalLinks = currentTask.external_links || [];
 setCurrentTask({
 ...updatedTask,
 external_links: updatedTask.external_links || currentExternalLinks
 });
 if (onTaskUpdate) onTaskUpdate(); 
 }
 } catch (error) {
 console.error("Error fetching updated task:", error);
 }
 };

 // Fetch active price change request
 const fetchActivePriceChange = async () => {
 try {
 console.log(' Fetching active price change for task:', task.id);
 const API = import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
 if (!API) {
 console.warn('No API URL for fetching active price change');
 return;
 }
 const response = await axios.get(`${API}/api/tasks/${task.id}/active-price-change`);
 console.log(' Active price change response:', response.data);
 setActivePriceChange(response.data);
 } catch (error) {
 if (error.response?.status !== 404) {
 console.error(" Error fetching active price change:", error);
 console.error("Response:", error.response?.data);
 } else {
 console.log(' No active price change found (404)');
 setActivePriceChange(null);
 }
 }
 };

 const handleConfirmTask = async () => {
 if (!window.confirm("Подтвердить выполнение задания? Средства будут переведены исполнителю.")) return;
 try {
 setConfirmingTask(true);
 const API = getApiUrl();
 const r = await axios.post(`${API}/api/tasks/${task.id}/confirm`);
 showToast(r.data?.b2b || task.is_b2b ? B2B_CONFIRM_TOAST : "Задание подтверждено! Оплата выпущена.", "success");
 if (onTaskUpdate) onTaskUpdate();
 onBack();
 } catch (error) {
 showToast(error.response?.data?.detail || "Ошибка при подтверждении задания", "error");
 } finally {
 setConfirmingTask(false);
 }
 };

 const handleSubmitDisputeFromDetails = async () => {
 if (!disputeFormDetails.reason.trim() || !disputeFormDetails.description.trim()) {
 showToast("Заполните все поля жалобы", "error");
 return;
 }
 if (!chatOtherUserId) {
 showToast("Не удалось определить пользователя для жалобы", "error");
 return;
 }
 const reasonLabels = {
 fraud: "Мошенничество",
 unethical: "Неэтичное поведение",
 threats: "Угрозы или оскорбления",
 rules_violation: "Нарушение правил платформы",
 personal_data: "Нарушение конфиденциальности данных",
 other: "Другое"
 };
 const fullDescription = `Причина: ${reasonLabels[disputeFormDetails.reason] || disputeFormDetails.reason}\n\n${disputeFormDetails.description}`;
 try {
 setDisputeSubmitting(true);
 const API = getApiUrl();
 await axios.post(`${API}/api/complaints`, {
 target_user_id: chatOtherUserId,
 task_id: task.id,
 description: fullDescription
 });
 showToast("Жалоба подана. Администрация рассмотрит её в течение 3 дней.", "success");
 setShowDisputeSection(false);
 setDisputeFormDetails({ reason: '', description: '' });
 } catch (error) {
 const errorMessage = error.response?.data?.detail || "Ошибка при подаче жалобы";
 showToast(errorMessage, "error");
 } finally {
 setDisputeSubmitting(false);
 }
 };

 const handleOpenDisputeEvidenceUpload = (event) => {
 const files = Array.from(event.target.files);
 const BLOCKED_EXTENSIONS = [
 '.exe', '.dll', '.bat', '.cmd', '.com', '.msi', '.scr', '.pif',
 '.sh', '.bash', '.zsh', '.ps1', '.psm1', '.psd1', '.vbs', '.js',
 '.jar', '.class', '.php', '.py', '.rb', '.pl', '.asp', '.aspx',
 '.cgi', '.htaccess', '.svg'
 ];
 const blockedFiles = files.filter(f => {
 const ext = '.' + f.name.toLowerCase().split('.').pop();
 return BLOCKED_EXTENSIONS.includes(ext);
 });
 if (blockedFiles.length > 0) {
 showToast(
 `Файлы не разрешены: ${blockedFiles.map(f =>f.name).join(', ')}`,
 "error"
 );
 event.target.value = '';
 return;
 }
 files.forEach(file => {
 const reader = new FileReader();
 reader.onload = (e) => {
 setOpenDisputeEvidence(prev => [...prev, { data: e.target.result, name: file.name, type: file.type }]);
 };
 reader.readAsDataURL(file);
 });
 };

 const handleSubmitOpenDisputeFromDetails = async () => {
 if (!openDisputeFormDetails.reason.trim() || !openDisputeFormDetails.description.trim()) {
 showToast("Заполните все поля", "error");
 return;
 }
 try {
 setOpenDisputeSubmitting(true);
 const API = getApiUrl();
 const disputeResp = await axios.post(`${API}/api/tasks/${task.id}/dispute`, {
 reason: openDisputeFormDetails.reason,
 description: openDisputeFormDetails.description,
 evidence_files: openDisputeEvidence.map(e =>e.data)
 });
 showToast(disputeResp.data?.message || "Спор открыт и передан администратору на рассмотрение", "success");
 setShowOpenDisputeSection(false);
 setOpenDisputeFormDetails({ reason: '', description: '' });
 setOpenDisputeEvidence([]);
 setCurrentTask({...currentTask, status: 'disputed'});
 if (onTaskUpdate) onTaskUpdate();
 } catch (error) {
 const errorMessage = error.response?.data?.detail || "Ошибка при открытии спора";
 showToast(errorMessage, "error");
 } finally {
 setOpenDisputeSubmitting(false);
 }
 };

 // Возврат задания на доработку заказчиком (доступно 1 раз, без спора)
 const handleRequestRevision = async () => {
 if (!revisionComment.trim()) {
 showToast("Опишите, что именно нужно исправить", "error");
 return;
 }
 try {
 setRevisionSubmitting(true);
 const API = getApiUrl();
 const response = await axios.post(`${API}/api/tasks/${task.id}/request-revision`, { comment: revisionComment });
 showToast(response.data?.message || "Задание возвращено исполнителю на доработку", "success");
 setShowRevisionSection(false);
 setRevisionComment('');
 setCurrentTask({...currentTask, status: 'revision_requested', customer_revision_used: true, revision_requested_by: 'customer'});
 if (onTaskUpdate) onTaskUpdate();
 } catch (error) {
 showToast(error.response?.data?.detail || "Ошибка при возврате на доработку", "error");
 } finally {
 setRevisionSubmitting(false);
 }
 };

 // Создание платежа для доплаты (заказчик) — TaskDetails scope
 const createPriceChangePayment = async () => {
 if (creatingPayment) return;
 try {
 setCreatingPayment(true);
 if (!activePriceChange || activePriceChange.status !== "confirmed") {
 showToast("Изменение цены не подтверждено", "error");
 return;
 }
 const priceDifference = activePriceChange.new_price - activePriceChange.old_price;
 if (priceDifference <= 0) { showToast("Доплата не требуется", "info"); return; }
 showToast("Создание платежа...", "info");
 const response = await axios.post(`${API}/tasks/${task.id}/price-change/payment?price_change_id=${activePriceChange.id}`, { amount: priceDifference });
 if (response.data.payment_url) {
 window.open(response.data.payment_url, '_blank');
 showToast(`Платеж на ${priceDifference}₽ создан. Завершите оплату в открывшемся окне.`, "success");
 } else {
 showToast("Платеж создан в тестовом режиме", "success");
 }
 await fetchActivePriceChange();
 } catch (error) {
 showToast(error.response?.data?.detail || "Ошибка при создании платежа.", "error");
 } finally {
 setCreatingPayment(false);
 }
 };

 // Request price change (customer only)
 const requestPriceChange = async () => {
 try {
 const API = getApiUrl();
 const response = await axios.post(`${API}/api/tasks/${task.id}/change-price`, {
 new_price: parseFloat(priceChangeData.newPrice),
 reason: priceChangeData.reason
 });
 
 showToast(response.data.message, "success");
 setShowPriceChange(false);
 fetchActivePriceChange();
 await fetchUpdatedTask(); // Ensure task data is up to date
 if (onTaskUpdate) onTaskUpdate();
 } catch (error) {
 if (error.message === 'API URL not configured') {
 showToast('Ошибка конфигурации приложения', 'error');
 } else {
 showToast(error.response?.data?.detail || "Ошибка при изменении цены", "error");
 }
 }
 };

 // Respond to price change (executor only)
 const respondToPriceChange = async (action, reason = "") => {
 try {
 const API = getApiUrl();
 const response = await axios.post(`${API}/api/tasks/${task.id}/respond-price-change`, {
 action,
 reason
 });
 
 showToast(response.data.message, "success");
 fetchActivePriceChange();
 
 // Update task price immediately if confirmed
 if (action === "confirm" && response.data.new_price) {
 const newPrice = response.data.new_price;
 task.price = newPrice;
 setCurrentTask({...currentTask, price: newPrice});
 }
 
 // Fetch updated task data to ensure everything is in sync
 await fetchUpdatedTask();
 
 if (onTaskUpdate) onTaskUpdate();
 } catch (error) {
 showToast(error.response?.data?.detail || "Ошибка при ответе на изменение цены", "error");
 }
 };

 const fetchMessages = async () => {
 try {
 // Get API base URL
 const API = import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
 if (!API || !task?.id) {
 console.error('Missing API URL or task ID:', { API, taskId: task?.id });
 setLoading(false);
 return;
 }
 
 const response = await axios.get(`${API}/api/tasks/${task.id}/messages`);
 setMessages(response.data);
 } catch (error) {
 console.error("Error fetching messages:", error);
 // Don't show error toast for network issues to avoid spam
 if (error.response?.status >= 500 || !error.response) {
 console.warn("Network/server error fetching messages, will retry");
 }
 } finally {
 setLoading(false);
 }
 };

 const sendMessage = async (e) => {
 e.preventDefault();
 if (!newMessage.trim()) return;

 try {
 // Get API base URL
 const API = import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
 if (!API) {
 showToast("Ошибка конфигурации", "error");
 return;
 }
 
 await axios.post(`${API}/api/tasks/${task.id}/messages`, {
 task_id: task.id,
 message: newMessage
 }).then(res => {
 if (res.data?.warning) showToast(res.data.warning, "warning");
 });
 setNewMessage("");
 fetchMessages();
 // Force scroll after sending message
 setTimeout(() => {
 scrollToBottom();
 }, 300);
 } catch (error) {
 console.error("Error sending message:", error);
 showToast("Ошибка при отправке сообщения", "error");
 }
 };

 // Simple single event listener - no multiple handlers
 const submitComplaintChat = async () => {
 if (!complaintTextChat.trim()) { showToast("Опишите суть жалобы", "error"); return; }
 if (!chatOtherUserId) { showToast("Не удалось определить пользователя", "error"); return; }
 try {
 setComplaintSubmittingChat(true);
 await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/complaints`, {
 target_user_id: chatOtherUserId,
 task_id: task.id,
 description: complaintTextChat.trim()
 });
 showToast("Жалоба подана. Администрация рассмотрит её в течение 3 дней.", "success");
 setComplaintTextChat("");
 setShowComplaintModalChat(false);
 setComplaintSentChat(true);
 } catch (error) {
 showToast(error.response?.data?.detail || "Ошибка при подаче жалобы", "error");
 } finally {
 setComplaintSubmittingChat(false);
 }
 };

 // Simple single event listener - no multiple handlers
 useEffect(() => {
 const fileInput = document.getElementById('mobile-file-input');
 if (fileInput) {
 const handleFileChange = (event) => {
 if (event.target.files && event.target.files.length > 0) {
 handleFileUpload(event);
 }
 };
 
 fileInput.addEventListener('change', handleFileChange, { passive: true });
 
 // Cleanup
 return () => {
 if (fileInput) {
 fileInput.removeEventListener('change', handleFileChange);
 }
 };
 }
 }, []);
 // ЕДИНСТВЕННЫЙ файловый обработчик
 const handleFileUpload = async (e) => {
 const files = Array.from(e.target.files);
 if (files.length === 0) return;

 const maxSize = 150 * 1024 * 1024; // 150MB
 const oversizedFiles = files.filter(f =>f.size >maxSize);
 if (oversizedFiles.length > 0) {
 showToast(`Файлы слишком большие (макс. 150МБ): ${oversizedFiles.map(f =>f.name).join(', ')}`, "error");
 return;
 }
 if (uploadFiles.length + uploadedRefs.length + files.length > 15) {
 showToast("Максимум 15 файлов", "error");
 return;
 }

 // Проверка расширений на фронтенде (пункт аудита #4)
 const BLOCKED_EXTENSIONS = [
 '.exe', '.dll', '.bat', '.cmd', '.com', '.msi', '.scr', '.pif',
 '.sh', '.bash', '.zsh', '.ps1', '.psm1', '.psd1', '.vbs', '.js',
 '.jar', '.class', '.php', '.py', '.rb', '.pl', '.asp', '.aspx',
 '.cgi', '.htaccess', '.svg'
 ];
 const blockedFiles = files.filter(f => {
 const ext = '.' + f.name.toLowerCase().split('.').pop();
 return BLOCKED_EXTENSIONS.includes(ext);
 });
 if (blockedFiles.length > 0) {
 showToast(
 `Файлы не разрешены для загрузки: ${blockedFiles.map(f =>f.name).join(', ')}. ` +
 'Разрешены: изображения, видео, аудио, PDF, документы Word/Excel.',
 "error"
 );
 setFileInputKey(prev =>prev + 1);
 return;
 }

 setFileProcessing(true);

 // Split into small (base64+compress) and large (chunked)
 const smallFiles = files.filter(f =>f.size < CHUNKED_THRESHOLD);
 const largeFiles = files.filter(f =>f.size >= CHUNKED_THRESHOLD);

 // Same legacy compressor for small files
 const compressFile = (file) =>new Promise((resolve) => {
 if (!file.type.startsWith('image/')) {
 const reader = new FileReader();
 reader.onload = (ev) =>resolve(ev.target.result);
 reader.readAsDataURL(file);
 return;
 }
 const img = new Image();
 const url = URL.createObjectURL(file);
 img.onload = () => {
 const maxDim = 2048;
 let { width, height } = img;
 if (width >maxDim || height >maxDim) {
 if (width >height) { height = Math.round(height * maxDim / width); width = maxDim; }
 else { width = Math.round(width * maxDim / height); height = maxDim; }
 }
 const canvas = document.createElement('canvas');
 canvas.width = width; canvas.height = height;
 canvas.getContext('2d').drawImage(img, 0, 0, width, height);
 URL.revokeObjectURL(url);
 resolve(canvas.toDataURL('image/jpeg', 0.82));
 };
 img.onerror = () => {
 const reader = new FileReader();
 reader.onload = (ev) =>resolve(ev.target.result);
 reader.readAsDataURL(file);
 };
 img.src = url;
 });

 try {
 // 1) process small files first
 if (smallFiles.length > 0) {
 const base64Files = await Promise.all(smallFiles.map(compressFile));
 setUploadFiles(prev => [...prev, ...base64Files]);
 setUploadFileNames(prev => [...prev, ...smallFiles.map(f => f.name)]);
 }

 // 2) chunked-upload large files with progress
 if (largeFiles.length > 0) {
 const controller = new AbortController();
 uploadAbortRef.current = controller;
 const totalBytes = largeFiles.reduce((s, f) =>s + f.size, 0) || 1;
 const initialItems = largeFiles.map(f => ({
 name: f.name, loaded: 0, total: f.size, percent: 0, status: "pending",
 }));
 setUploadProgressItems(initialItems);
 setUploadOverallPercent(0);

        // Создаём миниатюры для изображений/видео до начала загрузки
        const newThumbs = {};
        largeFiles.forEach(f => {
          if (f.type.startsWith('image/') || f.type.startsWith('video/')) {
            newThumbs[f.name + '_' + f.size] = URL.createObjectURL(f);
          }
        });
        if (Object.keys(newThumbs).length > 0) {
          setLargeThumbnails(prev => ({ ...prev, ...newThumbs }));
        }

 let bytesDoneBefore = 0;
 for (let i = 0; i < largeFiles.length; i++) {
 const f = largeFiles[i];
 setUploadProgressItems(prev =>prev.map((it, idx) =>idx === i ? { ...it, status: "uploading" } : it));
 try {
 const ref = await uploadFileChunked(f, {
 signal: controller.signal,
 onProgress: (p) => {
 setUploadProgressItems(prev =>prev.map((it, idx) =>idx === i ? { ...it, loaded: p.loaded, total: p.total, percent: p.percent } : it
 ));
 const overallLoaded = bytesDoneBefore + p.loaded;
 setUploadOverallPercent(Math.round((overallLoaded / totalBytes) * 100));
 },
 });
 setUploadedRefs(prev => [...prev, ref]);
 setUploadProgressItems(prev =>prev.map((it, idx) =>idx === i ? { ...it, status: "done", percent: 100 } : it));
 bytesDoneBefore += f.size;
 setUploadOverallPercent(Math.round((bytesDoneBefore / totalBytes) * 100));
 } catch (err) {
 const aborted = controller.signal.aborted || err?.name === "CanceledError" || err?.code === "ERR_CANCELED";
 setUploadProgressItems(prev =>prev.map((it, idx) =>idx === i ? { ...it, status: "error", error: aborted ? "Отменено" : (err?.message || "Ошибка") } : it
 ));
 if (aborted) break;
 }
 }
 uploadAbortRef.current = null;
 }

 showToast(`Загружено файлов: ${files.length}`, "success");
 setRenderKey(prev =>prev + 1);
 } catch (error) {
 console.error("Error uploading files:", error);
 showToast("Ошибка при загрузке файлов", "error");
 } finally {
 setFileProcessing(false);
 setFileInputKey(prev =>prev + 1);
 }
 };

 const cancelChunkedUpload = () => {
 if (uploadAbortRef.current) {
 uploadAbortRef.current.abort();
 uploadAbortRef.current = null;
 showToast("Загрузка отменена", "info");
 }
 };


 const completeTask = async () => {
 // Get external links from localStorage (in case state was reset)
 const savedLinks = localStorage.getItem(`external_links_${task.id}`);
 const externalLinksToSend = savedLinks ? JSON.parse(savedLinks) : externalLinks;

 const taskResultType = task.result_type || "media";
 const isTextResult = taskResultType === "text" || taskResultType === "action";

 if (isTextResult) {
 if (resultText.trim().length < 10) {
 showToast("Опишите результат выполнения задания (минимум 10 символов)", "error");
 return;
 }
 } else if (uploadFiles.length === 0 && uploadedRefs.length === 0 && externalLinksToSend.length === 0) {
 showToast("Пожалуйста, загрузите хотя бы один файл или добавьте ссылку", "error");
 return;
 }

 setCompletionLoading(true);
 showToast("Завершаем задание...", "info");

 try {
 const response = await axios.post(`${API}/tasks/${task.id}/complete`, {
 files: uploadFiles,
 file_names: uploadFileNames,
 uploaded_refs: uploadedRefs,
 external_links: externalLinksToSend,
 result_text: resultText.trim()
 });
 
 // Clear localStorage after successful completion
 localStorage.removeItem(`external_links_${task.id}`);
 setUploadedRefs([]);
 setUploadProgressItems([]);
 setUploadOverallPercent(0);
 setResultText("");
 
 showToast("Задание успешно выполнено!", "success");
 onTaskUpdate();
 onBack();
 
 // ИСПРАВЛЕНИЕ: Обновляем activePriceChange после изменения статуса
 fetchActivePriceChange();
 } catch (error) {
 showToast("Ошибка при завершении задания", "error");
 console.error("Task completion error:", error);
 } finally {
 setCompletionLoading(false);
 }
 };

 // Functions for external links
 const addExternalLink = () => {
 console.log(' addExternalLink STARTED');
 console.log(' Current externalLinks before:', externalLinks);
 
 if (!linkUrl.trim()) {
 showToast("Введите ссылку", "error");
 return;
 }
 
 const newLink = {
 url: linkUrl.trim(),
 title: linkTitle.trim() || "Большой файл",
 description: ""
 };
 
 console.log(' New link to add:', newLink);
 
 // Update state and localStorage
 const newArray = [...externalLinks, newLink];
 console.log(' Creating new array:', newArray);
 
 setExternalLinks(newArray);
 localStorage.setItem(`external_links_${task.id}`, JSON.stringify(newArray));
 
 // Clear form
 setLinkUrl('');
 setLinkTitle('');
 setShowLinkModal(false);
 showToast("Ссылка добавлена", "success");
 
 console.log(' addExternalLink FINISHED - saved to localStorage');
 
 // Check state after timeout
 setTimeout(() => {
 const saved = localStorage.getItem(`external_links_${task.id}`);
 console.log(' LocalStorage after 1 second:', saved);
 console.log(' State after 1 second:', externalLinks);
 }, 1000);
 };

 const removeExternalLink = (index) => {
 const newLinks = externalLinks.filter((_, i) =>i !== index);
 setExternalLinks(newLinks);
 localStorage.setItem(`external_links_${task.id}`, JSON.stringify(newLinks));
 showToast("Ссылка удалена", "info");
 };

 return (
 <div className="max-w-4xl mx-auto">
 <UploadProgressBar
 uploads={uploadProgressItems}
 overallPercent={uploadOverallPercent}
 onCancel={cancelChunkedUpload}
 onClose={() => { setUploadProgressItems([]); setUploadOverallPercent(0); }}
 />
 <div className="mb-6">
 <button
 onClick={onBack}
 className="mb-4 flex items-center gap-1.5 text-sm font-medium"
 style={{color:'var(--rv-teal)', background:'none', border:'none', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
 >
 ← Назад к заданиям
 </button>
 
 <div className="reversia-card">
 <h2 className="text-2xl font-bold mb-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>{task.title}</h2>
 <p className="text-sm mb-2 break-words" style={{color:'var(--rv-text-secondary)'}}>{task.location}</p>
 <p className="text-sm mb-4 leading-relaxed" style={{color:'var(--rv-text-primary)'}}>{task.description}</p>
 {task.deadline && (
 <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
 <span className="font-medium">Срок выполнения:</span>
 <span className="px-2 py-0.5 rounded" style={{backgroundColor: 'var(--reversia-beige)', color: 'var(--reversia-teal-dark)'}}>
 {new Date(task.deadline).toLocaleDateString('ru-RU', {day: 'numeric', month: 'long', year: 'numeric'})}
 </span>
 </div>
 )}
 
 {/* Navigation Button */}
 {task.latitude && task.longitude ? (
 <div className="mb-4">
 <button
 onClick={() => {
 // Try Yandex Maps first (better for Russia)
 const yandexUrl = `https://yandex.ru/maps/?whatshere%5Bpoint%5D=${task.longitude}%2C${task.latitude}&whatshere%5Bzoom%5D=17&l=map`;
 const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${task.latitude},${task.longitude}`;
 
 // Check if mobile device
 const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
 
 if (isMobile) {
 // Try to open native app first
 const yandexApp = `yandexmaps://maps.yandex.ru/?pt=${task.longitude},${task.latitude}&z=17`;
 const googleApp = `google.navigation:q=${task.latitude},${task.longitude}`;
 
 // Try Yandex app first, fallback to web
 try {
 window.location.href = yandexApp;
 setTimeout(() => {
 window.open(yandexUrl, '_blank');
 }, 500);
 } catch {
 window.open(yandexUrl, '_blank');
 }
 } else {
 // Desktop - open in new tab
 window.open(yandexUrl, '_blank');
 }
 }}
 className="reversia-btn reversia-btn-primary reversia-btn-md flex items-center gap-2"
 >Построить маршрут
 </button>
 <p className="text-xs text-gray-500 mt-1">
 {task.latitude.toFixed(4)}, {task.longitude.toFixed(4)}
 </p>
 </div>
 ) : (
 <div className="mb-4 p-3 bg-orange-50 rounded-md">
 <p className="text-sm text-orange-800">Координаты не указаны для этого задания
 </p>
 </div>
 )}
 
 {/* Service Type Info */}
 {task.service_type && task.service_type !== "general" && (
 <div className="mb-4 p-3 bg-blue-50 rounded-md">
 <div className="flex items-center justify-between flex-wrap gap-1">
 <span className="text-sm font-medium text-blue-800">
 {task.service_type === "secret_shopper" ? "Тайный покупатель" : 
 task.service_type === "grave_care" ? "Мемориальный сервис" :
 task.service_type === "mobile_journalist" ? "Мобильный корреспондент" : "Специальная услуга"}
 </span>
 <span className="text-xs text-blue-600">Комиссия: {task.commission_rate ? (task.commission_rate * 100).toFixed(0) : 15}%
 </span>
 </div>
 
 {/* Тип контента для журналистского задания */}
 {task.service_type === "mobile_journalist" && task.media_content_type && (
 <div className="mt-2 flex items-center gap-1.5">
 <span className="text-xs text-blue-700">Что снять:</span>
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
 style={{backgroundColor: 'rgba(32,178,170,0.12)', color: 'var(--reversia-teal)'}}>
 {task.media_content_type === "photo" && "Фото"}
 {task.media_content_type === "video" && "Видео"}
 {task.media_content_type === "photo_video" && "Фото и видео"}
 </span>
 </div>
 )}

 {/* Бейдж верифицированного журналиста в блоке типа задания */}
 {task.service_type === "mobile_journalist" && (
 <div className="mt-2">
 {/* Заказчик видит статус верификации исполнителя */}
 {user.role === "customer" && chatOtherProfile?.journalist_verified && (
 <div className="flex items-center gap-2 p-2 bg-green-100 rounded border border-green-200">
 <span className="text-xs text-green-800 font-medium">Исполнитель является верифицированным журналистом платформы</span>
 </div>
 )}
 {/* Исполнитель видит свой статус верификации */}
 {user.role === "executor" && user.journalist_verified && (
 <div className="flex items-center gap-2 p-2 bg-blue-100 rounded border border-blue-200">
 <span className="text-xs text-blue-800 font-medium">Вы верифицированный журналист — задание доступно</span>
 </div>
 )}
 {user.role === "executor" && !user.journalist_verified && task.verified_journalists_only && (
 <div className="p-2 bg-orange-100 rounded border border-orange-200">
 <p className="text-xs text-orange-800 font-medium">Задание только для верифицированных журналистов</p>
 <p className="text-xs text-orange-600 mt-0.5">Пройдите верификацию в разделе "Журналистика"</p>
 </div>
 )}
 </div>
 )}
 </div>
 )}

 {/* Reference Media: grave_care (подсказки) и general (фото к заданию) */}
 {(task.service_type === "grave_care" || task.service_type === "general") && task.reference_media && task.reference_media.length > 0 && (
 <div className="mb-4 p-4 bg-green-50 rounded-md border border-green-200">
 <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center">{task.service_type === "general" ? "Фотографии к заданию" : "Подсказки для поиска участка"}
 </h4>
 <p className="text-xs text-green-700 mb-3">{task.service_type === "general" ? "Заказчик приложил эти фото, чтобы вы могли оценить объём работы и стоимость задания на этапе обсуждения:" : "Заказчик предоставил эти изображения для помощи в поиске участка захоронения:"}
 </p>
 <div className="grid grid-cols-2 gap-3">
 {task.reference_media.map((media, index) => (
 <div key={index} className="relative bg-white rounded-lg overflow-hidden shadow-sm border border-green-300">
 {media.startsWith('data:image') ? (
 <div 
 className="relative group cursor-pointer"
 onClick={() =>setReferenceImageModal({ src: media, index: index + 1 })}
 >
 <img 
 src={media} 
 alt={`Подсказка ${index + 1}`}
 className="w-full h-40 object-contain bg-gray-50 hover:opacity-90 transition-opacity"
 />
 <div className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full font-medium shadow pointer-events-none">
 #{index + 1}
 </div>
 {/* Иконка увеличения при наведении */}
 <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-20 pointer-events-none">
 <div className="bg-white rounded-full p-2 shadow-lg">
 <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
 </div>
 </div>
 </div>
 ) : media.startsWith('data:video') ? (
 <div className="relative">
 <video 
 src={media} 
 className="w-full h-40 object-contain bg-gray-50"
 controls
 preload="metadata"
 />
 <div className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full font-medium shadow">
 #{index + 1}
 </div>
 </div>
 ) : null}
 </div>
 ))}
 </div>
 <p className="text-xs text-green-600 mt-3 flex items-center gap-1">
 <span className="font-medium">Нажмите на изображение</span>для просмотра в полном размере и скачивания
 </p>
 </div>
 )}
 
 <div className="flex justify-between items-center">
 <div className="text-lg font-bold text-green-600">{currentTask.price} ₽</div>
 <div className={`text-sm px-2 py-1 rounded ${
 task.status === "pending" ? "bg-yellow-100 text-yellow-800" :
 task.status === "discussing" ? "bg-orange-100 text-orange-800" :
 task.status === "accepted" ? "bg-blue-100 text-blue-800" :
 task.status === "completed" ? "bg-green-100 text-green-800" :
 task.status === "confirmed" ? "bg-purple-100 text-purple-800" :
 task.status === "revision_requested" ? "bg-amber-100 text-amber-800" :
 task.status === "disputed" ? "bg-orange-100 text-orange-900" :
 task.status === "dispute_resolved_customer" ? "bg-teal-100 text-teal-800" :
 "bg-gray-100 text-gray-800"
 }`}>
 {task.status === "pending" ? "Ожидает" :
 task.status === "discussing" ? "Обсуждается" :
 task.status === "accepted" ? "Принято исполнителем" :
 task.status === "completed" ? "Выполнено" :
 task.status === "confirmed" ? "Подтверждено" :
 task.status === "revision_requested" ? "На доработке" :
 task.status === "disputed" ? "Спор открыт" :
 task.status === "dispute_resolved_customer" ? "Спор разрешён" :
 taskStatusLabel(task.status)}
 </div>
 </div>
 </div>
 </div>

 <div className="grid md:grid-cols-1 gap-6">
{/* TZ8-6: чат открывается единой модалью (эталон раздела «Чат») */}
<div className="bg-white rounded-lg shadow-md overflow-hidden">
<button
type="button"
data-testid="taskdetails-open-chat-btn"
className="w-full p-4 flex justify-between items-center cursor-pointer select-none text-left"
style={{background:'none', border:'none'}}
onClick={() =>setChatCollapsed(false)}
>
<h3 className="text-lg font-semibold flex items-center gap-2"><MessageSquare size={18} style={{color:'var(--rv-teal)'}} />Чат и обсуждение задания</h3>
<span className="reversia-btn reversia-btn-teal reversia-btn-sm">Открыть чат</span>
</button>
</div>
{!chatCollapsed && (
<TaskChatModal
task={currentTask}
user={user}
showToast={showToast}
testId="taskdetails-chat-modal"
onClose={() =>setChatCollapsed(true)}
onChanged={onBack}
/>
)}

 {/* Price Confirmation Section for Customer */}
 {(user.role === "customer" || user.role === "admin") && task.status === "accepted" && (
 <div className="mt-6 bg-white p-6 rounded-lg shadow-md">
 <h3 className="text-lg font-semibold mb-4">Подтверждение выполнения</h3>
 <div className="bg-green-50 p-4 rounded-md">
 <h4 className="font-medium text-green-800 mb-2">Текущая цена задания: {currentTask.price}₽</h4>
 <p className="text-sm text-green-700 mb-4">Когда исполнитель выполнит задание и загрузит результаты, вы сможете подтвердить выполнение и произвести оплату.
 </p>
 <div className="text-xs text-green-600">Средства будут списаны только после вашего подтверждения качества работы
 </div>
 </div>
 </div>
 )}

 {/* File Upload (for executors) - move to separate section */}
 {user.role === "executor" && task.executor_id === user.id && task.status === "accepted" && (
 <div className="reversia-card">
 <h3 className="text-lg font-semibold mb-4">
 {(task.result_type === "text" || task.result_type === "action") ? "Отчёт о выполнении" : "Загрузка результатов"}
 </h3>

 {(task.result_type === "text" || task.result_type === "action") && (
 <div className="mb-4">
 <label className="block text-sm font-medium mb-1 text-gray-700">
 {task.result_type === "action" ? "Опишите, что было сделано" : "Текстовый отчёт"}
 </label>
 <textarea
 data-testid="result-text-input"
 rows="5"
 value={resultText}
 onChange={(e) => setResultText(e.target.value)}
 placeholder={task.result_type === "action"
 ? "Опишите выполненную работу: что сделано, какие были нюансы, что важно знать заказчику..."
 : "Напишите результат: найденная информация, ответ, разбор. Можно добавить ссылки на источники..."}
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--rv-teal,#0d9488)]"
 />
 <p className="text-xs text-gray-500 mt-1">Фото или файл можно приложить ниже по желанию.</p>
 </div>
 )}

 {/* ГЛАВНЫЙ СЧЕТЧИК с принудительным обновлением */}
 <div key={`counter-${renderKey}`} className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
 <div className="text-lg font-semibold text-blue-800">Загружено файлов: {uploadFiles.length + uploadedRefs.length}
 </div>
 <div className="text-sm text-blue-600 mt-1">Максимум 15 файлов по 150МБ каждый
 </div>
 </div>
 
 {/* ЗАГРУЗКА ФАЙЛОВ */}
 <div className="mb-4">
 <input
 key={fileInputKey}
 type="file"
 multiple
 onChange={handleFileUpload}
 className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
 />
 </div>

 {/* ОТОБРАЖЕНИЕ ЗАГРУЖЕННЫХ ФАЙЛОВ — сворачиваемый список */}
 {uploadFiles.length > 0 && (
 <div className="mb-4">
 <div className="flex items-center justify-between mb-2">
 <button
 type="button"
 onClick={() =>setUploadFilesCollapsed(v => !v)}
 className="flex items-center gap-2 text-sm font-medium text-green-700 hover:text-green-900"
 >
 <span>Загруженные файлы ({uploadFiles.length})</span>
 <span className="text-gray-400">{uploadFilesCollapsed ? '▼ Показать' : '▲ Скрыть'}</span>
 </button>
 <button
 onClick={() => {
 setUploadFiles([]);
 setUploadFileNames([]);
 showToast("Файлы очищены", "info");
 }}
 className="text-xs text-red-600 hover:text-red-800 underline"
 >Очистить все
 </button>
 </div>
 {!uploadFilesCollapsed && (
 <div className="space-y-3">
 {uploadFiles.map((fileData, index) => {
 const [mimeInfo, base64Data] = fileData.split(',');
 const mimeType = mimeInfo.match(/:(.*?);/)?.[1] || '';
 const isImage = mimeType.startsWith('image/');
 const isVideo = mimeType.startsWith('video/');
 const sizeEstimate = Math.round((base64Data.length * 3) / 4 / 1024 / 1024 * 10) / 10;
 
 return (
 <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
 <div className="flex-shrink-0">
 {isImage ? (
 <img
 src={fileData}
 alt={`Файл ${index + 1}`}
 className="w-16 h-16 object-cover rounded-md border"
 />
 ) : isVideo ? (
 <div className="w-16 h-16 bg-gray-200 rounded-md border flex items-center justify-center">
 <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
 </div>
 ) : (
 <div className="w-16 h-16 bg-blue-100 rounded-md border flex items-center justify-center">
 <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
 </div>
 )}
 </div>
 <div className="flex-1 min-w-0">
 <p className="text-sm font-medium text-gray-900 truncate" title={uploadFileNames[index] || ''}>
 {uploadFileNames[index] || `${isImage ? 'Изображение' : isVideo ? 'Видео' : 'Файл'} #${index + 1}`}
 </p>
 <p className="text-xs text-gray-500">~{sizeEstimate}МБ</p>
 </div>
            <button
              onClick={() => {
                setUploadFiles(prev => prev.filter((_, i) => i !== index));
                setUploadFileNames(prev => prev.filter((_, i) => i !== index));
                showToast("Файл удалён", "info");
              }}
              className="flex-shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50 rounded p-1 transition-colors"
              title="Удалить файл"
            >
              <XIcon size={16} />
            </button>
 </div>
 );
 })}
 </div>
 )}
 </div>
 )}

 
 {externalLinks.length > 0 && (
 <div className="mb-4">
 <h4 className="text-sm font-medium text-orange-700 mb-2">Внешние ссылки на файлы:
 </h4>
 <div className="space-y-2">
 {externalLinks.map((link, index) => (
 <div key={index} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
 <div className="flex-grow">
 <div className="font-medium text-sm">{link.title}</div>
 <div className="text-xs text-gray-600 truncate">{link.url}</div>
 </div>
 <button
 onClick={() =>removeExternalLink(index)}
 className="text-red-500 hover:text-red-700 p-1"
 >
 
 </button>
 </div>
 ))}
 </div>
 </div>
 )}

 {uploadedRefs.length > 0 && (
 <div data-testid="chunked-uploads-list" className="mb-4">
 <div className="flex items-center justify-between mb-2">
 <h4 className="text-sm font-medium text-teal-700">Крупные файлы ({uploadedRefs.length}):
 </h4>
 <button
 data-testid="chunked-uploads-clear-btn"
 onClick={() => {
 setUploadedRefs([]);
 setUploadProgressItems([]);
 setUploadOverallPercent(0);
 showToast("Крупные файлы очищены", "info");
 }}
 className="text-xs text-red-600 hover:text-red-800 underline"
 >Очистить
 </button>
 </div>
 <div className="space-y-2">
            {uploadedRefs.map((ref, idx) => {
              const thumbKey = ref.filename + '_' + ref.file_size;
              const thumbUrl = largeThumbnails[thumbKey];
              const isImg = ref.content_type?.startsWith('image/');
              const isVid = ref.content_type?.startsWith('video/');
              return (
                <div key={idx} className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg border border-teal-200 text-sm">
                  <div className="flex-shrink-0">
                    {thumbUrl && isImg ? (
                      <img src={thumbUrl} alt={ref.filename} className="w-14 h-14 object-cover rounded-md border border-teal-300" />
                    ) : thumbUrl && isVid ? (
                      <video src={thumbUrl} className="w-14 h-14 object-cover rounded-md border border-teal-300" muted playsInline />
                    ) : isVid ? (
                      <div className="w-14 h-14 bg-teal-100 rounded-md border border-teal-300 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-teal-600"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                      </div>
                    ) : (
                      <div className="w-14 h-14 bg-teal-100 rounded-md border border-teal-300 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-teal-600"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="font-medium truncate text-gray-900">{ref.filename}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {(ref.file_size / (1024 * 1024)).toFixed(1)} МБ
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setUploadedRefs(prev => prev.filter((_, i) => i !== idx));
                      showToast("Файл удалён", "info");
                    }}
                    className="flex-shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50 rounded p-1 transition-colors"
                    title="Удалить файл"
                  >
                    <XIcon size={16} />
                  </button>
                </div>
              );
            })}
 </div>
 </div>
 )}

 {/* КНОПКА ДЛЯ БОЛЬШИХ ФАЙЛОВ */}
 <div className="mb-4 flex gap-2">
 <button
 type="button"
 onClick={() =>setShowLinkModal(true)}
 className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 text-sm"
 >Файл большой? Дайте ссылку
 </button>
 </div>
 </div>
 )}

 {/* Secret Shopper Report — ПЕРЕД загрузкой файлов */}
 {user.role === "executor" && task.executor_id === user.id && task.status === "accepted" &&
 task.service_type === "secret_shopper" && task.report_template && (
 <div className="reversia-card">
 <h3 className="text-lg font-semibold mb-4">Отчет тайного покупателя</h3>
 <SecretShopperReport
 template={task.report_template}
 externalData={secretShopperReportData}
 onExternalChange={handleReportChange}
 onSubmit={async (reportData) => {
 try {
 await axios.post(`${API}/tasks/${task.id}/submit-report`, reportData);
 clearReportDraft();
 showToast("Отчет отправлен", "success");
 } catch (error) {
 showToast("Ошибка при отправке отчета", "error");
 }
 }}
 />
 </div>
 )}

 {/* Подсказка исполнителю для мемориального сервиса */}
 {user.role === "executor" && task.executor_id === user.id && task.status === "accepted" &&
 task.service_type === "grave_care" && (
 <div className="p-4 rounded-lg border-l-4" style={{backgroundColor: '#f0fdfa', borderLeftColor: 'var(--reversia-teal)'}}>
 <p className="text-sm font-medium mb-1" style={{color: 'var(--reversia-teal-dark)'}}>Совет по работе с заказчиком</p>
 <p className="text-sm text-gray-700">После выполнения задания опишите в чате состояние участка: что было сделано, что требует внимания при следующем посещении (трещины на плитке, облупившаяся краска, нехватка цветов и т.д.). Это помогает заказчику планировать будущие работы и выстраивать с вами долгосрочные отношения.
 </p>
 </div>
 )}

 {/* Кнопка Завершить задание — в самом низу всех блоков */}
 {user.role === "executor" && task.executor_id === user.id && task.status === "accepted" && (
 <div className="reversia-card">
 <button
 onClick={async () => {
 if (task.service_type === "secret_shopper" && task.report_template) {
 try {
 await axios.post(`${API}/tasks/${task.id}/submit-report`, secretShopperReportData);
 clearReportDraft();
 showToast("Отчет тайного покупателя отправлен", "success");
 } catch (error) {
 showToast("Ошибка отправки отчета, задание будет завершено", "warning");
 }
 }
 completeTask();
 }}
 disabled={(((task.result_type === "text" || task.result_type === "action") ? resultText.trim().length < 10 : (uploadFiles.length === 0 && uploadedRefs.length === 0 && externalLinks.length === 0))) || completionLoading || fileProcessing}
 className="reversia-btn reversia-btn-green reversia-btn-lg w-full justify-center"
 >
 {completionLoading ? (
 <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Завершаем...</>
 ) : fileProcessing ? (
 <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Обработка файлов...</>
 ) : task.service_type === "secret_shopper" ? (
 "Завершить задание с отчетом"
 ) : (
 "Завершить задание"
 )}
 </button>
 </div>
 )}

 {/* Special Service Components — только GraveCareChecklist (SecretShopperReport перемещён выше) */}
 {user.role === "executor" && task.executor_id === user.id && task.status === "accepted" && (
 <>
 {/* Grave Care Checklist */}
 {task.service_type === "grave_care" && task.checklist && (
 <div className="reversia-card">
 <h3 className="text-lg font-semibold mb-4">Чек-лист мемориального сервиса</h3>
 <GraveCareChecklist 
 checklist={task.checklist}
 onUpdate={async (updatedChecklist) => {
 try {
 const response = await axios.post(`${API}/tasks/${task.id}/update-checklist`, {
 checklist: updatedChecklist
 });
 
 // Обновляем локальные данные задания с сервера
 if (response.data.checklist) {
 task.checklist = response.data.checklist;
 }
 
 showToast("Чек-лист обновлен", "success");
 
 // Обновляем данные задания из родительского компонента
 if (onTaskUpdate) {
 onTaskUpdate();
 }
 } catch (error) {
 console.error("Ошибка обновления чек-листа:", error);
 showToast("Ошибка при обновлении чек-листа", "error");
 }
 }}
 />
 </div>
 )}

 {/* Secret Shopper Report перемещён выше (перед блоком загрузки файлов) */}
 </>
 )}

 {/* B2B / прямая оплата: цена согласована, платёжных шагов нет */}
 {isDirectPriceAgreed(task, activePriceChange) && (
 <div className="reversia-card flex items-start gap-3" data-testid="task-details-b2b-price-agreed" style={{background:'var(--rv-teal-light)', border:'1px solid #5EEAD4'}}>
 <BadgeCheck size={20} className="flex-shrink-0 mt-0.5" style={{color:'var(--rv-teal-dark)'}} />
 <p className="text-sm" style={{color:'var(--rv-teal-dark)'}}>
 <strong>Новая цена согласована: {fmtRub(activePriceChange.new_price)} ₽.</strong>{' '}
 Оплата — напрямую исполнителю по его реквизитам после выполнения.
 </p>
 </div>
 )}

 {/* P1: БЛОК ДОПЛАТЫ ДЛЯ ЗАКАЗЧИКА - Вынесен из чата (только эскроу-задания) */}
 {task.customer_id === user.id && needsSurcharge(task, activePriceChange) && (
 <div className="reversia-card border-l-4 border-teal-500 shadow-lg">
 <div className="flex items-start space-x-4">
 <div className="flex-shrink-0">
 <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{backgroundColor: 'var(--reversia-teal)'}}>
 <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
 </div>
 </div>
 
 <div className="flex-1">
 <div className="flex items-center space-x-2 mb-2">
 <h3 className="text-lg font-semibold text-gray-900">Требуется доплата</h3>
 <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">Ожидает оплаты
 </span>
 </div>
 
 <div className="space-y-3">
 <p className="text-sm text-gray-700">Исполнитель согласился на новую цену задания
 </p>
 
 <div className="bg-gray-50 p-4 rounded-md">
 <div className="flex justify-between items-center mb-2">
 <span className="text-sm text-gray-600">Первоначальная стоимость:</span>
 <span className="text-sm font-medium text-gray-900">{activePriceChange.old_price}₽</span>
 </div>
 <div className="flex justify-between items-center mb-2">
 <span className="text-sm text-gray-600">Новая стоимость:</span>
 <span className="text-sm font-medium" style={{color: 'var(--reversia-teal)'}}>{activePriceChange.new_price}₽</span>
 </div>
 <div className="border-t border-gray-200 pt-2 mt-2">
 <div className="flex justify-between items-center">
 <span className="text-base font-semibold text-gray-900">Сумма доплаты:</span>
 <span className="text-xl font-bold" style={{color: 'var(--reversia-teal)'}}>
 +{activePriceChange.new_price - activePriceChange.old_price}₽
 </span>
 </div>
 </div>
 </div>

 {activePriceChange.reason && (
 <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
 <p className="text-xs text-blue-700 font-medium mb-1">Причина изменения цены:</p>
 <p className="text-sm text-blue-900">{activePriceChange.reason}</p>
 </div>
 )}

 <button
 onClick={createPriceChangePayment}
 disabled={creatingPayment}
 className={`w-full py-3 px-6 rounded-md font-medium text-white transition-all ${
 creatingPayment 
 ? 'bg-gray-400 cursor-not-allowed' 
 : 'hover:shadow-lg transform hover:-translate-y-0.5'
 }`}
 style={{
 backgroundColor: creatingPayment ? undefined : 'var(--reversia-teal)'
 }}
 >
 {creatingPayment ? (
 <span className="flex items-center justify-center">
 <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Создание платежа...
 </span>
 ) : (
 <span className="flex items-center justify-center">
 <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>Оплатить доплату {activePriceChange.new_price - activePriceChange.old_price}₽
 </span>
 )}
 </button>

 <p className="text-xs text-gray-500 text-center">После оплаты исполнитель сможет приступить к работе
 </p>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Results Display */}
 {(((task.media_files && task.media_files.length > 0) && !((task.reopened_at || task.previous_executor_id) && ['open', 'discussing', 'accepted'].includes(task.status))) || (task.external_links && task.external_links.length > 0) || ((task.result_text && task.result_text.trim()) && !((task.reopened_at || task.previous_executor_id) && ['open', 'discussing', 'accepted'].includes(task.status)))) && (
 <div className="reversia-card" style={{padding: 0, overflow: 'hidden'}}>
 <div
 className="flex justify-between items-center cursor-pointer select-none px-6 py-4"
 style={{borderBottom: resultsCollapsed ? 'none' : '1px solid #e5e7eb'}}
 onClick={() =>setResultsCollapsed(v => !v)}
 >
 <h3 className="text-lg font-semibold">Результаты выполнения
 {task.media_files && task.media_files.length > 0 && <span className="ml-2 text-sm font-normal text-gray-500">({task.media_files.length} файлов)</span>}
 </h3>
 <span className="text-gray-400 text-sm font-medium">
 {resultsCollapsed ? '▼ Развернуть' : '▲ Свернуть'}
 </span>
 </div>
 {!resultsCollapsed && (
 <div className="px-6 py-4">
 <TaskResultsViewer 
 taskId={task.id} 
 mediaFiles={task.media_files} 
 externalLinks={task.external_links}
 resultText={task.result_text}
 userRole={task.customer_id === user.id ? 'customer' : 'executor'}
 taskStatus={task.status}
 showToast={showToast}
 instanceId={`detailed-${task.id}`}
 onTaskUpdate={onTaskUpdate}
 />
 </div>
 )}
 </div>
 )}

 {/* Special Service Results Display */}
 {task.status === "completed" || task.status === "confirmed" ? (
 <>
 {/* Grave Care Results */}
 {task.service_type === "grave_care" && task.checklist && (
 <div className="reversia-card">
 <h3 className="text-lg font-semibold mb-4">Результаты мемориального сервиса</h3>
 <div className="bg-gray-50 p-4 rounded-lg">
 <div className="space-y-3">
 {task.checklist.map((item) => (
 <div key={item.id} className="flex items-start space-x-3 bg-white p-3 rounded-md border border-gray-200">
 <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${item.completed ? 'bg-green-500' : 'bg-gray-300'}`}>
 {item.completed && <Check className="w-3 h-3 text-white" />}
 </div>
 <div className="flex-1">
 <span className={`text-sm ${item.completed ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
 {item.label}
 </span>
 {item.completed && (
 <span className="ml-2 text-xs text-green-600">Выполнено</span>
 )}
 </div>
 </div>
 ))}
 </div>
 
 {/* Статистика */}
 <div className="mt-4 pt-4 border-t border-gray-200">
 <p className="text-sm text-gray-600">Выполнено: <strong className="text-green-600">{task.checklist.filter(item =>item.completed).length}</strong>из <strong>{task.checklist.length}</strong>пунктов
 </p>
 </div>
 </div>
 </div>
 )}
 </>
 ) : null}

 {/* Secret Shopper Results - Show if report exists regardless of status */}
 {task.service_type === "secret_shopper" && task.report_data && (
 <div className="reversia-card">
 <h3 className="text-lg font-semibold mb-4">Отчет тайного покупателя</h3>
 <div className="space-y-4">
 {(() => {
 // Строим карту ключ → метка из шаблона задания
 const fieldLabelMap = {};
 if (task.report_template?.required_fields) {
 task.report_template.required_fields.forEach(f => {
 fieldLabelMap[f.name] = f.label;
 });
 }
 const hardcodedLabels = {
 anonymous_performer: 'Анонимный исполнитель',
 service_quality: 'Качество обслуживания (1-5)',
 staff_behavior: 'Поведение персонала',
 environment: 'Обстановка заведения',
 mandatory_photo_proof: 'Фото-подтверждение',
 };
 return Object.entries(task.report_data).map(([key, value]) => (
 <div key={key} className="border-b pb-2">
 <dt className="text-sm font-medium text-gray-700">
 {hardcodedLabels[key] || fieldLabelMap[key] || key}
 </dt>
 <dd className="text-sm text-gray-900 mt-1">
 {key === 'anonymous_performer' ? (value ? 'Да' : 'Нет') :
 key === 'mandatory_photo_proof' ? (
 <img src={value} alt="Фото-подтверждение" className="max-w-xs rounded-md" />
 ) : value}
 </dd>
 </div>
 ));
 })()}
 </div>
 </div>
 )}
 </div>

 {/* Блок: Задание на доработке (статус revision_requested) */}
 {currentTask.status === "revision_requested" && (
 <div className="reversia-card" style={{background: '#fffbeb', border: '1px solid #fcd34d'}}>
 {user.id === currentTask.executor_id ? (
 <RevisionSubmitBlock
 task={currentTask}
 showToast={showToast}
 onTaskUpdate={() => { onTaskUpdate(); onBack(); }}
 revFiles={revisionFiles}
 setRevFiles={setRevisionFiles}
 />
 ) : (
 <>
 <h3 className="text-lg font-semibold mb-2" style={{color: '#92400e'}}>Задание отправлено исполнителю на доработку</h3>
 <p className="text-sm" style={{color: '#78350f'}}>Администратор направил работу исполнителю на исправление. Вы получите уведомление, когда исполнитель завершит доработку.
 </p>
 </>
 )}
 </div>
 )}

 {/* Блок: Решено в пользу заказчика — выбор действия */}
 {currentTask.status === "dispute_resolved_customer" && (user.role === "customer" || user.role === "admin") && user.id === currentTask.customer_id && (
 <DisputeCustomerChoiceBlock task={currentTask} showToast={showToast} onTaskUpdate={onTaskUpdate} />
 )}

 {/* Блок подтверждения/жалобы — только для заказчика когда задание выполнено */}
 {(user.role === "customer" || user.role === "admin") && currentTask.status === "completed" && (
 <div className="reversia-card" style={{background: '#f0fdf4', border: '1px solid #bbf7d0'}}>
 <h3 className="text-lg font-semibold mb-2" style={{color: '#15803d'}}>Подтверждение выполнения</h3>
 <p className="text-sm mb-3" style={{color: '#166534'}}>Исполнитель отметил задание как выполненное. Ознакомьтесь с результатами в разделе выше и выберите действие.
 </p>

 {/* Таймер 48 часов */}
 {confirmCountdown !== null && (() => {
 if (confirmCountdown > 0) {
 const totalSec = Math.floor(confirmCountdown / 1000);
 const h = Math.floor(totalSec / 3600);
 const m = Math.floor((totalSec % 3600) / 60);
 const s = totalSec % 60;
 const pad = (n) =>String(n).padStart(2, '0');
 const urgent = confirmCountdown < 6 * 60 * 60 * 1000; // < 6 часов
 return (
 <div className={`mb-4 px-4 py-3 rounded-lg flex items-center gap-3 ${urgent ? 'bg-orange-50 border border-orange-200' : 'bg-blue-50 border border-blue-200'}`}>
 <div className={`text-2xl font-mono font-bold ${urgent ? 'text-orange-600' : 'text-blue-700'}`}>
 {pad(h)}:{pad(m)}:{pad(s)}
 </div>
 <div className="text-sm" style={{color: urgent ? '#9a3412' : '#1e40af'}}>
 {urgent
 ? 'Осталось совсем мало времени для принятия решения'
 : 'Осталось для принятия решения (рекомендуем не затягивать)'}
 </div>
 </div>
 );
 } else {
 // 48 часов истекло, автоподтверждение через 72ч
 const autoInMs = 72 * 60 * 60 * 1000 - (Date.now() - new Date(currentTask.completed_at || currentTask.updated_at).getTime());
 const autoH = Math.max(0, Math.floor(autoInMs / 1000 / 3600));
 const autoM = Math.max(0, Math.floor((autoInMs / 1000 % 3600) / 60));
 return (
 <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-300">
 <div className="text-sm font-semibold text-orange-800">Срок рекомендованного решения истёк.
 </div>
 <div className="text-sm text-orange-700 mt-1">
 {autoInMs > 0
 ? `Задание будет подтверждено автоматически примерно через ${autoH} ч ${autoM} мин, если вы не примете решение.`
 : 'Задание будет подтверждено автоматически в ближайшее время.'}
 </div>
 </div>
 );
 }
 })()}
 {!showDisputeSection && !showOpenDisputeSection && !showRevisionSection ? (
 <>
 {/* Мини-инструкция: порядок действий если результат не устраивает */}
 <div className="mb-4 px-4 py-3 rounded-lg" style={{background:'#eff6ff', border:'1px solid #bfdbfe'}} data-testid="revision-flow-instruction">
 <p className="text-sm font-semibold mb-1" style={{color:'#1e40af'}}>Если результат не устраивает — порядок действий:</p>
 <ol className="text-sm space-y-0.5 list-decimal list-inside" style={{color:'#1e3a8a'}}>
 <li>Верните задание на доработку с комментарием — без участия администратора (доступно 1 раз{currentTask.customer_revision_used ? ', уже использовано' : ''}).</li>
 <li>Если после доработки результат снова не подходит — откройте спор, решение примет администратор.</li>
 </ol>
 </div>
 <div className="flex flex-wrap gap-3">
 <button
 data-testid="confirm-task-details-btn"
 onClick={handleConfirmTask}
 disabled={confirmingTask}
 className="reversia-btn reversia-btn-green reversia-btn-md"
 >
 {confirmingTask ? "Подтверждаем..." : "Подтвердить выполнение"}
 </button>
 {!currentTask.customer_revision_used && (
 <button
 data-testid="request-revision-details-btn"
 onClick={() =>setShowRevisionSection(true)}
 className="reversia-btn reversia-btn-md"
 style={{backgroundColor: '#d97706', color: '#fff', border: 'none'}}
 >Вернуть на доработку
 </button>
 )}
 <button
 data-testid="open-dispute-details-btn"
 onClick={() =>setShowOpenDisputeSection(true)}
 className="reversia-btn reversia-btn-red reversia-btn-md"
 style={{backgroundColor: '#dc2626'}}
 >Открыть спор
 </button>
 <button
 data-testid="submit-complaint-details-btn"
 onClick={() =>setShowDisputeSection(true)}
 className="reversia-btn reversia-btn-orange reversia-btn-md"
 >Подать жалобу
 </button>
 </div>
 </>
 ) : showRevisionSection ? (
 <div className="space-y-3" data-testid="revision-request-section">
 <p className="text-sm font-medium" style={{color:'#92400e'}}>Вернуть на доработку — если исполнителю нужно исправить или дополнить результат</p>
 <p className="text-xs" style={{color:'#78350f'}}>Доступно 1 раз. Если после доработки результат снова не устроит — откройте спор, решение примет администратор. У исполнителя будет 48 часов на исправление.</p>
 <textarea
 data-testid="revision-comment-input"
 value={revisionComment}
 onChange={(e) =>setRevisionComment(e.target.value)}
 placeholder="Конкретно укажите, что именно нужно исправить или дополнить (например: не хватает 4 фотографий заднего двора)..."
 rows={3}
 className="w-full p-2 border border-gray-300 rounded-md text-sm"
 />
 <div className="flex gap-2">
 <button
 data-testid="submit-revision-request-btn"
 onClick={handleRequestRevision}
 disabled={revisionSubmitting}
 className="reversia-btn reversia-btn-md"
 style={{backgroundColor: '#d97706', color: '#fff', border: 'none'}}
 >
 {revisionSubmitting ? "Отправляем..." : "Вернуть на доработку"}
 </button>
 <button
 onClick={() => { setShowRevisionSection(false); setRevisionComment(''); }}
 className="reversia-btn reversia-btn-md bg-gray-500 hover:bg-gray-600 text-white"
 >Отмена
 </button>
 </div>
 </div>
 ) : showOpenDisputeSection ? (
 <div className="space-y-3">
 <p className="text-sm text-red-700 font-medium">Открыть спор — если результат не соответствует техзаданию</p>
 <select
 value={openDisputeFormDetails.reason}
 onChange={(e) =>setOpenDisputeFormDetails({...openDisputeFormDetails, reason: e.target.value})}
 className="w-full p-2 border border-gray-300 rounded-md text-sm"
 >
 <option value="">Выберите причину</option>
 <option value="quality_issues">Низкое качество материалов</option>
 <option value="missing_files">Не хватает файлов</option>
 <option value="wrong_content">Не соответствует техзаданию</option>
 <option value="deadline_missed">Нарушен срок сдачи</option>
 <option value="technical_issues">Технические проблемы с материалами</option>
 <option value="other">Другое</option>
 </select>
 <textarea
 value={openDisputeFormDetails.description}
 onChange={(e) =>setOpenDisputeFormDetails({...openDisputeFormDetails, description: e.target.value})}
 placeholder="Опишите подробно, что именно не соответствует договоренностям..."
 rows={3}
 className="w-full p-2 border border-gray-300 rounded-md text-sm"
 />
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Файлы-доказательства (необязательно)</label>
 <input
 type="file"
 multiple
 accept="image/*,video/*,.pdf"
 onChange={handleOpenDisputeEvidenceUpload}
 className="w-full text-sm text-gray-600 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
 />
 {openDisputeEvidence.length > 0 && (
 <div className="mt-2 space-y-1">
 {openDisputeEvidence.map((f, i) => (
 <div key={i} className="flex items-center justify-between bg-red-50 px-2 py-1 rounded text-xs text-gray-700">
 <span className="truncate max-w-xs">{f.name}</span>
 <button type="button" onClick={() =>setOpenDisputeEvidence(prev =>prev.filter((_, idx) =>idx !== i))} className="ml-2 text-red-500 hover:text-red-700">x</button>
 </div>
 ))}
 </div>
 )}
 </div>
 <div className="flex gap-2">
 <button
 onClick={handleSubmitOpenDisputeFromDetails}
 disabled={openDisputeSubmitting}
 className="reversia-btn reversia-btn-red reversia-btn-md"
 style={{backgroundColor: '#dc2626'}}
 >
 {openDisputeSubmitting ? "Отправляем..." : "Открыть спор"}
 </button>
 <button
 onClick={() => { setShowOpenDisputeSection(false); setOpenDisputeFormDetails({ reason: '', description: '' }); setOpenDisputeEvidence([]); }}
 className="reversia-btn reversia-btn-md bg-gray-500 hover:bg-gray-600 text-white"
 >Отмена
 </button>
 </div>
 </div>
 ) : (
 <div className="space-y-3">
 <p className="text-sm text-orange-700 font-medium">Подать жалобу — если исполнитель нарушил правила платформы</p>
 <select
 value={disputeFormDetails.reason}
 onChange={(e) =>setDisputeFormDetails({...disputeFormDetails, reason: e.target.value})}
 className="w-full p-2 border border-gray-300 rounded-md text-sm"
 >
 <option value="">Выберите причину</option>
 <option value="fraud">Мошенничество</option>
 <option value="unethical">Неэтичное поведение</option>
 <option value="threats">Угрозы или оскорбления</option>
 <option value="rules_violation">Нарушение правил платформы</option>
 <option value="personal_data">Нарушение конфиденциальности данных</option>
 <option value="other">Другое</option>
 </select>
 <textarea
 value={disputeFormDetails.description}
 onChange={(e) =>setDisputeFormDetails({...disputeFormDetails, description: e.target.value})}
 placeholder="Опишите нарушение подробно..."
 rows={3}
 className="w-full p-2 border border-gray-300 rounded-md text-sm"
 />
 <div className="flex gap-2">
 <button
 onClick={handleSubmitDisputeFromDetails}
 disabled={disputeSubmitting}
 className="reversia-btn reversia-btn-orange reversia-btn-md"
 >
 {disputeSubmitting ? "Отправляем..." : "Подать жалобу"}
 </button>
 <button
 onClick={() => { setShowDisputeSection(false); setDisputeFormDetails({ reason: '', description: '' }); }}
 className="reversia-btn reversia-btn-md bg-gray-500 hover:bg-gray-600 text-white"
 >Отмена
 </button>
 </div>
 </div>
 )}
 </div>
 )}

 {/* Review Section */}
 {task.status === "confirmed" && (
 <div className="bg-white p-6 rounded-lg shadow-md">
 <ReviewSection task={task} user={user} />
 </div>
 )}

 {/* МОДАЛЬНОЕ ОКНО ДЛЯ ДОБАВЛЕНИЯ ССЫЛКИ - TASKDETAILS */}
 {showLinkModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
 <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
 <h3 className="text-lg font-semibold mb-4">Добавить ссылку на большой файл</h3>
 
 <div className="space-y-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">Ссылка на файл:
 </label>
 <input
 type="url"
 value={linkUrl}
 onChange={(e) =>setLinkUrl(e.target.value)}
 placeholder="https://drive.google.com/... или https://disk.yandex.ru/..."
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">Название (необязательно):
 </label>
 <input
 type="text"
 value={linkTitle}
 onChange={(e) =>setLinkTitle(e.target.value)}
 placeholder="Например: Интервью 10 минут"
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 
 <div className="text-xs text-gray-500">
 <p className="mb-1">Поддерживаются:</p>
 <p>• Google Drive, Яндекс.Диск</p>
 <p>• Dropbox, YouTube</p>
 <p>• WeTransfer, Mega</p>
 </div>
 </div>
 
 <div className="flex gap-2 mt-6">
 <button
 onClick={addExternalLink}
 className="reversia-btn reversia-btn-primary flex-1 py-2 justify-center"
 >Добавить ссылку
 </button>
 <button
 onClick={() => {
 setShowLinkModal(false);
 setLinkUrl('');
 setLinkTitle('');
 }}
 className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-400"
 >Отмена
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Reference Image Modal - Модальное окно для подсказок */}
 {referenceImageModal && (
 <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
 <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
 {/* Заголовок */}
 <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-green-50">
 <h3 className="text-lg font-semibold text-green-800">Подсказка для поиска участка #{referenceImageModal.index}
 </h3>
 <button
 onClick={() =>setReferenceImageModal(null)}
 className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none hover:bg-gray-200 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
 >
 ×
 </button>
 </div>
 
 {/* Изображение в полном размере */}
 <div className="p-6 bg-gray-50">
 <img 
 src={referenceImageModal.src} 
 alt={`Подсказка ${referenceImageModal.index}`}
 className="w-full h-auto max-h-[70vh] object-contain rounded shadow-lg bg-white"
 />
 
 <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded text-sm text-green-800">
 <p className="font-medium mb-1">Используйте это изображение для поиска участка захоронения</p>
 <p className="text-xs">Вы можете скачать изображение для удобства, нажав кнопку ниже</p>
 </div>
 </div>
 
 {/* Кнопки */}
 <div className="flex gap-3 p-4 bg-gray-50 border-t border-gray-200">
 <button
 onClick={() =>setReferenceImageModal(null)}
 className="flex-1 bg-gray-500 text-white px-4 py-3 rounded-md text-sm font-medium hover:bg-gray-600 transition-colors"
 >Закрыть
 </button>
 <button
 onClick={() => {
 try {
 // Преобразуем base64 в blob для надежного скачивания
 const base64Data = referenceImageModal.src.split(',')[1];
 const mimeType = referenceImageModal.src.split(',')[0].split(':')[1].split(';')[0];
 
 // Декодируем base64
 const byteCharacters = atob(base64Data);
 const byteNumbers = new Array(byteCharacters.length);
 for (let i = 0; i < byteCharacters.length; i++) {
 byteNumbers[i] = byteCharacters.charCodeAt(i);
 }
 const byteArray = new Uint8Array(byteNumbers);
 const blob = new Blob([byteArray], { type: mimeType });
 
 // Создаем URL для blob
 const blobUrl = URL.createObjectURL(blob);
 
 // Скачиваем
 const link = document.createElement('a');
 link.href = blobUrl;
 link.download = `подсказка_${referenceImageModal.index}.jpg`;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 
 // Освобождаем память
 setTimeout(() =>URL.revokeObjectURL(blobUrl), 100);
 } catch (error) {
 console.error('Ошибка скачивания:', error);
 alert('Ошибка при скачивании изображения');
 }
 }}
 className="flex-1 bg-green-600 text-white px-4 py-3 rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
 >Скачать изображение
 </button>
 </div>
 </div>
 </div>
 )}
    <CookieBanner />
 </div>

 );
};

// Review Section Component
// Create Task Map Component
// Admin Media Files Section Component
// Consent Management Component (152-ФЗ)
// Performer Agreement Component - Updated for YuKassa compliance
// Platform Rules Component - Based on provided document
// Company Information Components
// Help and FAQ Component
// About Company Component
// Contacts Component
// App Review Modal Component (отзывы о приложении)
// Refund Policy Component
export default App;// Force recompile Mon Sep 8 18:57:42 UTC 2025

